/**
 * Словарь интерфейса приложения покупателя.
 *
 * Ключ — осмысленная фраза, а не «screen.feed.title.1»: по ключу должно быть
 * видно, что это за строка, иначе перевод делается вслепую. Русский текст
 * лежит здесь же и служит запасным вариантом: пропущенный перевод показывает
 * русскую строку, а не пустое место и не ключ.
 *
 * Переводится интерфейс. Тексты самих акций — название, приз, правила —
 * переводит партнёр в кабинете, и их подставляет сервер: иначе у одной акции
 * было бы два перевода, наш и его.
 */
import type { Lang } from './lang';

export type Dict = Record<string, string>;

const en: Dict = {
  // ─── меню и каркас ───
  Акции: 'Campaigns',
  Эфиры: 'Live draws',
  'Мои участия': 'My entries',
  Мои: 'Mine',
  Баллы: 'Points',
  Профиль: 'Profile',
  Скан: 'Scan',
  Сканировать: 'Scan code',
  'О проекте': 'About',
  'Как это работает': 'How it works',
  Войти: 'Sign in',
  Уведомления: 'Notifications',
  'Поиск по акциям и брендам': 'Search campaigns and brands',

  // ─── лента ───
  'Все страны': 'All countries',
  Все: 'All',
  'Идут сейчас': 'Running now',
  'Скоро стартуют': 'Starting soon',
  Итоги: 'Results',
  Бренды: 'Brands',
  'Ничего не нашлось': 'Nothing found',
  'Пока нет активных акций': 'No active campaigns yet',
  Ещё: 'More',
  'По умолчанию': 'Default',
  'Скоро закончатся': 'Ending soon',
  Новые: 'Newest',
  'Больше участников': 'Most entries',

  // ─── полоса важного ───
  'Ближайший розыгрыш': 'Next draw',
  'Срок перенесён': 'Date moved',
  'На этой неделе': 'This week',
  'Почему этому можно верить': 'Why you can trust this',
  'Любой розыгрыш пересчитывается в браузере': 'Any draw can be recomputed in your browser',
  'Смотреть акцию': 'Open campaign',
  'Показать новые': 'Show new ones',
  'Открыть отчёт розыгрыша': 'Open the draw report',
  'Где это видно': 'Where to see it',

  // ─── эфиры ───
  'В эфире': 'On air',
  Скоро: 'Soon',
  Состоялись: 'Completed',
  'розыгрыши платформы: что идёт сейчас, что будет и чем закончилось':
    'draws across the platform: what is on now, what is next and how it ended',
  'Отчёт о розыгрыше': 'Draw report',
  'Пересчитать самому': 'Recompute it yourself',
  Участвовать: 'Enter',
  'Розыгрышей пока нет': 'No draws yet',

  // ─── акция ───
  Условия: 'Terms',
  Участники: 'Entries',
  Правила: 'Rules',
  Честность: 'Fairness',
  Призы: 'Prizes',
  'Отсканировать код': 'Scan a code',
  'Пройти квиз': 'Take the quiz',
  'Отметить день': 'Check in today',
  'Пригласить друга': 'Invite a friend',
  Напомнить: 'Remind me',
  Напомним: 'Reminder on',
  'Призы проверены платформой.': 'Prizes verified by the platform.',

  // ─── общее ───
  участник: 'entry',
  участника: 'entries',
  участников: 'entries',
  билет: 'ticket',
  билета: 'tickets',
  билетов: 'tickets',
  приз: 'prize',
  приза: 'prizes',
  призов: 'prizes',
  идёт: 'live',
  'Не загрузилось': 'Failed to load',
  Повторить: 'Retry',
  Отмена: 'Cancel',
  Понятно: 'Got it',
  Далее: 'Next',

  // ─── шапка ленты, подписка, вопросы ───
  Архив: 'Archive',
  Конкурсы: 'Contests',
  'Платформа, которая создаёт доверие': 'A platform that earns trust',
  'Промо-акции брендов, результат которых можно пересчитать самому: победителей определяет формула от публичного сида, а не обещание организатора.':
    'Promo campaigns whose outcome you can recompute yourself: winners are decided by a formula over a public seed, not by the organiser’s word.',
  акция: 'campaign',
  акции: 'campaigns',
  акций: 'campaigns',
  бренд: 'brand',
  бренда: 'brands',
  брендов: 'brands',
  страна: 'country',
  страны: 'countries',
  стран: 'countries',

  'Попробуй другую сферу или страну — или сбрось фильтры и посмотри всё.':
    'Try another category or country — or reset the filters and see everything.',
  'Как только бренд запустит акцию в твоём регионе, она появится здесь.':
    'As soon as a brand launches a campaign in your country, it shows up here.',

  'Вопросы и ответы': 'Questions and answers',
  'коротко и по делу': 'short and to the point',
  'Как участвовать?': 'How do I take part?',
  'Найди код: под крышкой, на этикетке, в чеке или на полке — где именно, написано на странице акции. Отсканируй его в приложении: за розыгрыш ты получишь билет с номером, в мгновенной механике — результат сразу. Один код участвует один раз.':
    'Find the code: under the cap, on the label, on the receipt or on the shelf — the campaign page says exactly where. Scan it in the app: a raffle gives you a numbered ticket, an instant mechanic gives the result right away. Each code counts once.',
  'Откуда мне знать, что розыгрыш честный?': 'How do I know the draw is fair?',
  'Результат считает формула, а не человек. До розыгрыша публикуется список билетов, свёрнутый в хеш, и обещание сида; в день розыгрыша сид раскрывается — это публичный внешний источник, например тираж гослото, который организатор не выбирает. Дальше победители получаются пересчётом, и этот пересчёт можно повторить самому, прямо в браузере.':
    'A formula decides the outcome, not a person. Before the draw we publish the ticket list folded into a hash and a commitment to the seed; on the day of the draw the seed is revealed — a public external source, such as a state lottery result, which the organiser does not choose. Winners follow by computation, and you can repeat that computation yourself, right in the browser.',
  'Когда и где я получу приз?': 'When and where do I get the prize?',
  'Мгновенные призы и баллы приходят сразу. Вещевой приз нужно оформить — на это даётся срок, он указан в правилах акции; дальше бренд готовит приз и выдаёт его в своей точке. Статус виден в «Моих участиях» на каждом шаге: оформлен, готов, получен.':
    'Instant prizes and points arrive immediately. A physical prize has to be claimed — the deadline is stated in the campaign rules; then the brand prepares it and hands it over at its own location. The status is visible in “My entries” at every step: claimed, ready, received.',
  'Выдаёт приз бренд — он же организатор акции. Платформа следит за сроками и показывает, кто и что обещал.':
    'The brand hands out the prize — it is also the campaign organiser. The platform tracks the deadlines and shows who promised what.',
  'Это лотерея? Надо что-то платить?': 'Is this a lottery? Do I have to pay?',
  'Нет и нет. Это рекламные акции брендов: участие бесплатное, билет нельзя купить, а ставок здесь не бывает. Платформа не берёт денег с участников — за проведение акции платит бренд.':
    'No and no. These are brand promotions: taking part is free, tickets cannot be bought and there are no bets. The platform takes no money from participants — the brand pays for running the campaign.',
  'Что будет с моими данными?': 'What happens to my data?',
  'Почта нужна для входа — её брендам не передают. Анкету бренд может запросить, но только из закрытого списка полей и только с твоего подтверждения: ты видишь значения до передачи и можешь отказаться, код при этом не тратится. Кому и что ты передал, видно в профиле.':
    'Your email is for signing in and is never passed to brands. A brand may request profile fields, but only from a closed list and only with your confirmation: you see the values before they are shared and can refuse — the code is not spent. What you shared and with whom is visible in your profile.',
  'Мои согласия': 'My consents',
  'Политика данных': 'Data policy',

  'Новости и акции': 'News and campaigns',
  Готово: 'Done',
  'Адрес в списке. Первое письмо придёт, когда появится что-то стоящее: новые акции, итоги розыгрышей, важное по платформе. Отписаться можно одной ссылкой в любом из них.':
    'You are on the list. The first letter comes when there is something worth sending: new campaigns, draw results, platform news. One link in any of them unsubscribes you.',
  'Узнавай об акциях первым': 'Be first to know about campaigns',
  'Пишем редко и по делу: что стартовало в твоей стране, где розыгрыш на этой неделе и чем он закончился. Без рекламы ради рекламы — её хватает и без нас.':
    'We write rarely and to the point: what started in your country, which draw is this week and how it ended. No advertising for the sake of advertising — there is enough of that already.',
  почта: 'email',
  'Почта для новостей': 'Email for news',
  'Минуту…': 'One moment…',
  Подписаться: 'Subscribe',
  'Адрес нужен только для писем об акциях. Брендам он не передаётся — им вообще не видно, кто на что подписан.':
    'The address is only used for campaign emails. Brands never receive it — they cannot see who subscribed to what.',
  'Не получилось подписаться': 'Could not subscribe',
  'условия открываются по ссылке бренда': 'terms open through the brand’s link',

  // ─── архив ───
  'акции, которые уже прошли: сид опубликован, результат пересчитывается':
    'campaigns that have ended: the seed is published, the result can be recomputed',
  'Архив пока пуст': 'The archive is empty so far',
  'Сюда попадают акции, которые закончились. Первая появится здесь, как только завершится хотя бы одна.':
    'Campaigns land here once they end. The first one shows up as soon as any campaign finishes.',
  'В этом промежутке ничего нет': 'Nothing in this range',
  'Попробуй другую страну или раздвинь даты.': 'Try another country or widen the dates.',
  'акций проведено': 'campaigns run',
  'призовых мест': 'prize places',
  Сбросить: 'Reset',
  розыгрыш: 'draw',
  'Отчёт розыгрыша': 'Draw report',
  'Страница акции': 'Campaign page',
  Показано: 'Showing',
  из: 'of',
  с: 'from',
  по: 'to',
  'Весь архив открывается после входа — это тот же вход, что для участия, по коду из письма. Страница любой акции и отчёт розыгрыша при этом открыты всем и без него: доказательство прятать нельзя.':
    'The full archive opens after you sign in — the same sign-in as for taking part, by a code from an email. Every campaign page and draw report stays open to everyone without it: proof must not be hidden.',

  // ─── подвал ───
  'Акции по сферам': 'Campaigns by category',
  Участнику: 'For participants',
  Проверка: 'Verification',
  Магазины: 'Stores',
  Напитки: 'Drinks',
  Продукты: 'Groceries',
  Кафе: 'Cafés',
  Аптеки: 'Pharmacies',
  Электроника: 'Electronics',
  Заправки: 'Fuel stations',
  Банки: 'Banks',
  Туризм: 'Travel',
  Авиакомпании: 'Airlines',
  Подписки: 'Subscriptions',
  Связь: 'Telecom',
  'Все сферы': 'All categories',
  'Акции и шансы': 'Campaigns and odds',
  'Вся лента целиком': 'The whole feed',
  'Билеты и призы': 'Tickets and prizes',
  'Архив акций': 'Campaign archive',
  'Что уже прошло': 'What has ended',
  'Баллы и ранги': 'Points and ranks',
  'За что начисляют': 'How they are earned',
  'Сканировать код': 'Scan a code',
  'С упаковки или чека': 'From a pack or receipt',
  'Анкета и согласия': 'Profile and consents',
  'Кому переданы данные': 'Who received your data',
  'Как это проверяется': 'How it is verified',
  'Провабли-фейр': 'Provably fair',
  'Что хранится и зачем': 'What is stored and why',
  'Кто за что отвечает': 'Who is responsible for what',
  'Что это и кому': 'What it is and for whom',
  'Кабинет партнёра': 'Partner console',
  'Вход для брендов': 'Sign-in for brands',
  'Пилотный статус': 'Pilot status',
  'Чего проект не обещает': 'What the project does not promise',
  'Условия использования': 'Terms of use',
  'Политика конфиденциальности': 'Privacy policy',
  'Акции носят рекламный характер и не являются лотереей. Организатором каждой акции выступает бренд, указанный в её правилах; ALLSWIPES обеспечивает проведение и проверяемость результата. Условия конкретной акции — в её правилах, подписанных хешем, и они не меняются после запуска.':
    'Campaigns are promotional and are not a lottery. Each campaign is run by the brand named in its rules; ALLSWIPES provides the mechanics and the verifiability of the result. The terms of a specific campaign are in its rules, signed with a hash, and they do not change after launch.',

  'Платформа прозрачных промо-акций': 'The transparent promo campaign platform',

  // ─── фильтры и полоса важного ───
  Порядок: 'Sort',
  Механики: 'Mechanics',
  Нашлось: 'Found',
  'По этим условиям ничего нет': 'Nothing matches these filters',
  'до розыгрыша': 'until the draw',
  осталось: 'left',
  'Участников меньше объявленного минимума — дата сдвинута, условия те же.':
    'Fewer participants than the announced minimum — the date moved, the terms did not.',
  'Посмотреть историю переносов': 'See the history of moves',
  'Запустились за последние семь дней — коды уже в магазинах.':
    'Launched in the last seven days — the codes are already in stores.',
  'Список билетов и сид публикуются до розыгрыша. Результат проверяется без нас — формулой, а не словом.':
    'The ticket list and the seed are published before the draw. The result is verified without us — by a formula, not by a promise.',
  Платформа: 'Platform',

  // ─── сферы и страны: подписи приходят от сервера на русском ───
  Сферы: 'Categories',
  Страны: 'Countries',
  Гипермаркеты: 'Hypermarkets',
  Супермаркеты: 'Supermarkets',
  Другое: 'Other',
  Красота: 'Beauty',
  Одежда: 'Clothing',
  Таджикистан: 'Tajikistan',
  Узбекистан: 'Uzbekistan',
  Казахстан: 'Kazakhstan',
  Кыргызстан: 'Kyrgyzstan',
  Туркменистан: 'Turkmenistan',
  Азербайджан: 'Azerbaijan',
  Грузия: 'Georgia',
  Армения: 'Armenia',
  Турция: 'Türkiye',
  Монголия: 'Mongolia',

  Назад: 'Back',
  Сохранить: 'Save',
  Закрыть: 'Close',
  '+{n} {points} · день {day}': '+{n} {points} · day {day}',
  '+{n} баллов': '+{n} points',
  '+{n} баллов сверх обычных': '+{n} points on top of the usual',
  ': продолжишь с того же места. День считается по календарю акции, поэтому отметиться дважды за сутки нельзя даже с двух устройств.': ': you continue from where you left off. The day follows the campaign calendar, so you cannot check in twice a day even from two devices.',
  'JPEG, PNG, WebP, MP4 или WebM': 'JPEG, PNG, WebP, MP4 or WebM',
  'https://… или загрузи файл': 'https://… or upload a file',
  '{brand} просит эти данные для участия в акции «{campaign}». Перечень полей есть и в правилах акции — пункт 7.': '{brand} asks for this data to enter «{campaign}». The list of fields is also in the campaign rules, clause 7.',
  '{limit}. Ограничение в правилах акции, пункт 1.6 — оно не появляется в момент отказа.': '{limit}. The limit is in the campaign rules, clause 1.6 — it does not appear only when you are refused.',
  '{n} {prizes} выдано': '{n} {prizes} handed out',
  '{n} {tickets} в игре': '{n} {tickets} in play',
  '{n} баллов': '{n} points',
  '{n} баллов за анкету': '{n} points for the profile',
  '{n} дней на оформление': '{n} days to claim',
  '{n} из 5': '{n} out of 5',
  '{n} мин': '{n} min',
  '{n} ответа (обезличенно)': '{n} answers (anonymised)',
  '{prize} · выдаётся в точке партнёра': '{prize} · handed out at the partner\'s location',
  '{qn} {questions} — {rn} {points} за участие.': '{qn} {questions} — {rn} {points} for taking part.',
  '{shown} из {total}': '{shown} of {total}',
  'Акции создаются в отдельном кабинете партнёра. Доступ выдаёт владелец бренда.': 'Campaigns are created in the separate partner console. Access is granted by the brand owner.',
  'Акции этого бренда приостановлены платформой.': 'This brand\'s campaigns are suspended by the platform.',
  'Акция': 'Campaign',
  'Акция не найдена': 'Campaign not found',
  'Акция проведена учебным алгоритмом {algo}. Он небезопасен и больше не используется; пересчёт показан, чтобы исторический результат остался проверяемым.': 'This campaign was run with the learning algorithm {algo}. It is not secure and is no longer used; the recalculation is shown so the historical result stays verifiable.',
  'Алгоритм': 'Algorithm',
  'Анкета': 'Profile',
  'Анкета сохранена': 'Profile saved',
  'Баланс': 'Balance',
  'Баллы начисляются за каждый принятый код. Баланс — это сумма всей истории ниже, а не отдельное число: так он не может разойтись с операциями.': 'Points are awarded for every accepted code. The balance is the sum of the history below, not a separate number, so it cannot drift away from the operations.',
  'Без «@»': 'Without the «@»',
  'Без названия': 'Untitled',
  'Без ограничений по числу участий': 'No limit on the number of entries',
  'Билет получен': 'Ticket received',
  'Билетов': 'Tickets',
  'Билетов зафиксировано': 'Tickets frozen',
  'Браузер не дал посчитать хеш. Открой страницу по https.': 'The browser would not compute the hash. Open the page over https.',
  'Бренд': 'Brand',
  'Бренд видит': 'The brand sees',
  'Бренд запрашивает данные': 'The brand asks for data',
  'Бренд не найден': 'Brand not found',
  'Бренд приложил доказательство призового фонда, мы его посмотрели. Это не гарантия выдачи — за неё отвечает организатор, — но обещание больше не висит в воздухе.': 'The brand attached proof of the prize pool and we looked at it. This is not a guarantee of delivery — that is on the organiser — but the promise is no longer hanging in the air.',
  'Бренд проверен платформой: мы подтвердили, кто стоит за акциями': 'The brand is verified by the platform: we confirmed who stands behind the campaigns',
  'Бренд просит данные ниже. Ты видишь, что именно уйдёт — передача добровольна, отказ ничего не стоит, а согласие можно отозвать в профиле.': 'The brand asks for the data below. You see exactly what will be shared — sharing is voluntary, refusing costs nothing, and consent can be withdrawn in your profile.',
  'В день розыгрыша': 'On draw day',
  'В конце приёма кодов': 'When code redemption closes',
  'В конце цепочки — {prize}.': 'At the end of the chain — {prize}.',
  'В открытых списках участников виден только номер билета и обезличенная метка — ни почта, ни номер не публикуются никогда.': 'Public participant lists show only the ticket number and an anonymised label — neither email nor phone is ever published.',
  'В публичных списках': 'In public lists',
  'В розыгрыше номер билета сразу попадает в открытый список. В мгновенных механиках приз приходит в ту же секунду — его посчитал сервер по заранее зафиксированному сиду.': 'In a raffle the ticket number goes into the public list at once. In instant mechanics the prize arrives the same second — computed by the server from a seed committed in advance.',
  'В точке выдачи назови номер билета и подтверди личность.': 'At the pickup point give your ticket number and confirm your identity.',
  'Важное': 'Highlights',
  'Верификатор': 'Verifier',
  'Вернуться к акции': 'Back to the campaign',
  'Вероятности открыты и не меняются во время акции.': 'The odds are public and do not change during the campaign.',
  'Видео': 'Video',
  'Видео акции': 'Campaign video',
  'Витрина обмена — этап 3 в плане переноса. Пока баллы копятся и видны бренду как показатель вовлечения.': 'The exchange is stage 3 of the roadmap. For now points accumulate and the brand sees them as an engagement metric.',
  'Возможно, акция уже закончилась.': 'The campaign may have ended.',
  'Возможно, ссылка устарела.': 'The link may be out of date.',
  'Войди, чтобы видеть баллы': 'Sign in to see your points',
  'Войди, чтобы увидеть свои билеты': 'Sign in to see your tickets',
  'Войти по почте': 'Sign in by email',
  'Войти через Telegram · скоро': 'Sign in with Telegram · soon',
  'Войти, чтобы голосовать': 'Sign in to vote',
  'Войти, чтобы пройти': 'Sign in to take part',
  'Вопрос {n}': 'Question {n}',
  'Вопрос {n} из {total}': 'Question {n} of {total}',
  'Все акции': 'All campaigns',
  'Все дни пройдены': 'Every day completed',
  'Все работы': 'All entries',
  'Вставь сохранённый текст правил': 'Paste the rules text you saved',
  'Вход': 'Sign in',
  'Вход через Telegram скоро появится. Пока входи по почте.': 'Telegram sign-in is coming. For now sign in by email.',
  'Входим по адресу почты — на него придёт код. Пароль не нужен. Почта же привязывает твои билеты и призы, и без неё нельзя сменить страну.': 'You sign in with your email — the code arrives there. No password needed. Your email also ties your tickets and prizes to you, and without it you cannot change country.',
  'Входные данные розыгрыша': 'Draw inputs',
  'Выбери вариант': 'Pick an option',
  'Выбери цифру': 'Pick a digit',
  'Выдача приза': 'Prize handover',
  'Выигрышей': 'Wins',
  'Выйти': 'Sign out',
  'Голос не прошёл': 'Vote not counted',
  'Голос учтён': 'Vote counted',
  'Голоса кончились': 'No votes left',
  'Голоса скрыты до конца голосования: видимый счётчик тянет голоса к лидеру.': 'Vote counts are hidden until voting ends: a visible counter pulls votes towards the leader.',
  'Голосов на человека': 'Votes per person',
  'Голосование до': 'Voting until',
  'Голосовать': 'Vote',
  'Д{n}': 'D{n}',
  'Дата розыгрыша': 'Draw date',
  'Два поля, которые нужны при выдаче приза: как к тебе обратиться и как записано в документе. Остальное бренд спросит сам, если его акции это нужно, — и ты увидишь, что именно передаёшь.': 'Two fields needed when a prize is handed over: how to address you, and how it is written in your ID. Anything else the brand will ask for if its campaign needs it — and you will see exactly what you share.',
  'Для участия нужно передать: {fields}. Перед передачей ты увидишь сами значения и сможешь отказаться — код при этом не потратится.': 'To take part you need to share: {fields}. Before sharing you will see the values themselves and can refuse — the code is not spent.',
  'До {n} {friends}.': 'Up to {n} {friends}.',
  'До старта': 'Until the start',
  'Доказательство открывается после входа': 'The proof opens after you sign in',
  'Доказательство призового фонда': 'Proof of the prize pool',
  'Другие акции': 'Other campaigns',
  'Ежедневный вход': 'Daily check-in',
  'Если ты сохранил правила себе, вставь их сюда — браузер посчитает хеш и сравнит с опубликованным. Текст никуда не отправляется.': 'If you saved the rules, paste them here — the browser computes the hash and compares it with the published one. The text is never sent anywhere.',
  'Есть куда расти': 'Room to grow',
  'Ещё код': 'Another code',
  'Жди розыгрыш или получи приз сразу': 'Wait for the draw or get a prize right away',
  'Забрал приз': 'Prize collected',
  'Забрать день {day} · +{n}': 'Claim day {day} · +{n}',
  'Забрать: {point}': 'Collect at: {point}',
  'Загружаем…': 'Loading…',
  'Загрузить фото QR-кода': 'Upload a photo of the QR code',
  'Занято {taken} из {spots}. Места раздаются по очереди — по времени подтверждения кода на сервере, без розыгрыша.': '{taken} of {spots} taken. Slots go in order — by the time the code is confirmed on the server, with no draw.',
  'Запасные': 'Reserves',
  'Запасные разыграны тем же сидом и в том же проходе, что победители, и опубликованы сразу. Если приз не забрали в срок, место переходит следующему по списку — это не новый розыгрыш, а шаг по уже опубликованному порядку.': 'Reserves are drawn with the same seed in the same pass as the winners and published immediately. If a prize is not collected in time, the place goes to the next on the list — that is not a new draw but a step along the published order.',
  'Заполнять не обязательно. Эти поля никуда не уходят сами: бренд получит только то, что ты отдельно подтвердишь при участии в его акции.': 'Filling this in is optional. These fields go nowhere on their own: a brand receives only what you confirm separately when entering its campaign.',
  'Заходи каждый день — награда растёт. Срыв не наказывается.': 'Come back every day and the reward grows. Missing a day is not punished.',
  'Зачем: {why}': 'Why: {why}',
  'Заявка отправлена партнёру': 'Request sent to the partner',
  'Здесь появится расписание: мгновенные механики в эфир не попадают — у них нет даты, к которой можно прийти.': 'The schedule will appear here: instant mechanics do not go on air — they have no date to come to.',
  'И оставь отзыв — он виден бренду в статистике.': 'And leave a review — the brand sees it in its statistics.',
  'Изменить почту': 'Change email',
  'Иллюстрация': 'Illustration',
  'Инстаграм': 'Instagram',
  'История': 'History',
  'Источник сида': 'Seed source',
  'Исход посчитал сервер по сиду, хеш которого опубликован до старта акции. Сам сид раскроется после окончания — тогда результат можно будет пересчитать.': 'The outcome was computed by the server from a seed whose hash was published before the campaign started. The seed itself is revealed after it ends — then the result can be recomputed.',
  'Исход считает сервер по заранее зафиксированному сиду.': 'The server computes the outcome from a seed committed in advance.',
  'К акции': 'To the campaign',
  'К акции →': 'To the campaign →',
  'К акциям': 'To campaigns',
  'Кабинет бренда': 'Brand console',
  'Как всё прошло?': 'How did it go?',
  'Как оценивают призы': 'How prizes are rated',
  'Как участвовать': 'How to take part',
  'Как это проверить': 'How to check this',
  'Камера появится здесь': 'The camera will appear here',
  'Квиз': 'Quiz',
  'Квиз не найден': 'Quiz not found',
  'Квиз уже пройден — {n} {points} начислены тогда же. Вторая попытка не даётся.': 'The quiz is already done — {n} {points} were awarded back then. There is no second attempt.',
  'Когда платформа объявит конкурс, он появится здесь — с условиями, сроками и работами участников.': 'When the platform announces a contest it will appear here — with the terms, the dates and the entries.',
  'Код акции': 'Campaign code',
  'Код из письма': 'Code from the email',
  'Код отправлен на {to}. Введи шесть цифр.': 'The code was sent to {to}. Enter the six digits.',
  'Я компания — хочу проводить акции': 'I am a company — I want to run campaigns',
  'Компании': 'For companies',
  'Проводить акции на платформе может компания, с которой мы договорились. Поэтому кабинет не заводится по кнопке — расскажи о себе, и мы ответим.': 'Campaigns on the platform are run by companies we have agreed to work with. That is why no console appears at the press of a button — tell us about yourself and we will reply.',
  'Это не регистрация и не очередь: мы читаем каждую заявку сами.': 'This is neither a sign-up nor a queue: we read every request ourselves.',
  'Компания': 'Company',
  'Фамилия': 'Surname',
  'Рабочая почта': 'Work email',
  'Чем занимаетесь и какую акцию хотите провести': 'What you do and the campaign you have in mind',
  'Отправить заявку': 'Send the request',
  'Не получилось отправить заявку': 'The request did not go through',
  'Заявка отправлена': 'Request sent',
  'Получили. Ответим на {email} в ближайшие дни — письмом от живого человека, а не автоматом.': 'Got it. We will reply to {email} within a few days — a letter from a person, not a robot.',
  'Кабинет откроется по одноразовой ссылке: пароль ты придумаешь сам, и его не будем знать ни мы, ни кто-либо ещё.': 'The console opens through a one-time link: you choose the password, and nobody else will know it — us included.',
  'Вернуться к акциям': 'Back to campaigns',
  'Вход и регистрация': 'Sign in or sign up',
  'Отдельной регистрации нет: введи почту — если аккаунта ещё нет, он заведётся сам.': 'There is no separate sign-up: enter your email, and if you have no account yet it will be created for you.',
  'Пароль не нужен, на почту придёт код. Почта привязывает твои билеты и призы, и без неё нельзя сменить страну.': 'No password needed — a code comes to your inbox. Your email ties your tickets and prizes together, and you cannot change country without it.',
  'Продолжить по почте': 'Continue with email',
  'Продолжить с Google': 'Continue with Google',
  'Тоже и вход, и регистрация. Если на этой почте уже есть аккаунт, попадёшь в него — билеты и баллы на месте.': 'Also both at once. If an account already exists for that address you will land in it — tickets and points intact.',
  'Войти через Google': 'Sign in with Google',
  'Не получилось завершить вход. Вернись на экран входа и попробуй ещё раз.': 'The sign-in did not finish. Go back to the sign-in screen and try again.',
  'Это твой прежний аккаунт: та же почта, что и при входе по коду. Билеты, баллы и призы на месте.': 'This is your existing account — the same address you used with the emailed code. Your tickets, points and prizes are all here.',
  'мгновенно': 'instant',
  'Получить': 'Get it',
  'Получаем…': 'Getting it…',
  'Открыть предложение': 'Open the offer',
  'Предложения закончились': 'All offers taken',
  'Выдано {taken} из {limit}. Осталось {left}.': '{taken} of {limit} taken. {left} left.',
  'Выдано {taken}. Количество не ограничено.': '{taken} taken. No cap.',
  'Ты уже взял это предложение — ссылка выше та же самая.': 'You have already taken this offer — the link above is the same one.',
  'Где получить': 'Where to collect',
  'Призы и сколько их осталось': 'Prizes and how many are left',
  'осталось {n}': '{n} left',
  'разобрали': 'all gone',
  'выдано {issued} из {count}': '{issued} of {count} handed out',
  'Приз напечатан на упаковке производителем. Платформа его не выбирает — она считает выданное, и это число видно всем.': 'The prize is printed on the pack by the manufacturer. The platform does not pick it — it counts what has been handed out, and that number is public.',
  'Где забрать': 'Where to collect',
  'Адреса объявлены до старта и входят в правила акции.': 'The addresses are published before the start and form part of the campaign rules.',
  'Код действует ещё {left}': 'The code is valid for another {left}',
  'Срок кода истёк — запроси новый.': 'The code has expired — request a new one.',
  'Отправить код снова': 'Send the code again',
  'Код привязывается к твоему аккаунту — иначе мы не сможем показать билет и приз.': 'The code is tied to your account — otherwise we cannot show you the ticket and the prize.',
  'Код принят': 'Code accepted',
  'Код с упаковки или из чека': 'Code from the pack or the receipt',
  'Команда': 'Team',
  'Кому ты передал данные': 'Who you shared data with',
  'Конкурс': 'Contest',
  'Конкурс не найден': 'Contest not found',
  'Конкурсов сейчас нет': 'No contests right now',
  'Контакт': 'Contact',
  'Контакты': 'Contacts',
  'Контакты сохранены': 'Contacts saved',
  'Корень не совпал': 'The root did not match',
  'Корень списка билетов': 'Ticket list root',
  'Коротко': 'In brief',
  'Кто голосует': 'Who votes',
  'Лента покажет акции, которые идут здесь.': 'The feed will show campaigns running here.',
  'Лента показывает акции, которые идут в твоей стране.': 'The feed shows campaigns running in your country.',
  'Мест': 'Places',
  'Места': 'Slots',
  'Место определила очередь, а не случай: его получили первые {spots} по времени подтверждения кода на сервере. Счётчик мест публичный — это и есть проверка.': 'Your slot was decided by the queue, not by chance: the first {spots} by server confirmation time got one. The slot counter is public — that is the check.',
  'Можно взять значение с сайта тиража и вставить сюда: пересчёт пойдёт по нему, а не по нашей копии.': 'You can take the value from the lottery\'s own site and paste it here: the recalculation will use it, not our copy.',
  'НЕТ': 'NO',
  'На картинке не нашёлся QR-код. Попробуй другое фото или введи код руками.': 'No QR code was found in the image. Try another photo or type the code by hand.',
  'Набор собран — «{prize}» твой. Приз гарантирован за полный набор, без розыгрыша; оформить его можно в «Мои участия».': 'The set is complete — «{prize}» is yours. The prize is guaranteed for a full set, with no draw; claim it in «My entries».',
  'Набор собран — приз «{prize}» гарантирован, без розыгрыша.': 'The set is complete — the prize «{prize}» is guaranteed, with no draw.',
  'Награда начисляется, только когда друг': 'The reward is credited only when your friend',
  'Награда обоим: +{you} тебе, +{friend} другу.': 'A reward for both: +{you} for you, +{friend} for your friend.',
  'Название': 'Title',
  'Название команды': 'Team name',
  'Найди код': 'Find a code',
  'Напоминание снято': 'Reminder removed',
  'Напомним заранее': 'We will remind you in advance',
  'Напомним о старте акции, о розыгрыше за день и о результате.': 'We will remind you about the start, about the draw a day before, and about the result.',
  'Начать квиз · +{n}': 'Start the quiz · +{n}',
  'Начислено {n} {points} — за участие, независимо от числа верных.': '{n} {points} awarded — for taking part, regardless of how many were right.',
  'Не получилось': 'It did not work',
  'Не получилось войти': 'Could not sign in',
  'Не получилось отметиться': 'Could not check in',
  'Не получилось отправить': 'Could not send',
  'Не получилось отправить код': 'Could not send the code',
  'Не получилось отправить ответы': 'Could not send the answers',
  'Не получилось оформить': 'Could not claim',
  'Не получилось посчитать хеш': 'Could not compute the hash',
  'Не получилось принять приглашение': 'Could not accept the invitation',
  'Не получилось прочитать картинку. Введи код руками — это всегда работает.': 'Could not read the image. Type the code by hand — that always works.',
  'Не получилось сохранить': 'Could not save',
  'Не сейчас': 'Not now',
  'Не удалось загрузить данные': 'Could not load the data',
  'Не удалось открыть': 'Could not open',
  'Не удалось пересчитать': 'Could not recompute',
  'Не удалось проверить код': 'Could not verify the code',
  'Не удалось проверить список': 'Could not verify the list',
  'Не указано': 'Not set',
  'Не чаще раза в день': 'No more than once a day',
  'Не чаще раза в месяц': 'No more than once a month',
  'Не чаще раза в неделю': 'No more than once a week',
  'Не чаще раза в час': 'No more than once an hour',
  'Необязательно': 'Optional',
  'Неплохо': 'Not bad',
  'Ни одна работа не прошла проверку.': 'No entry passed moderation.',
  'Новые коды не принимаются. Правила и результаты прошедших розыгрышей остаются здесь и проверяются как обычно.': 'New codes are not accepted. The rules and the results of past draws stay here and are verified as usual.',
  'Номер попадёт в открытый список участников и в розыгрыш.': 'The number goes into the public participant list and into the draw.',
  'Номер телефона можно добавить в профиле после входа. Подтверждение номера появится позже — тогда бренды смогут запрашивать подтверждённый номер как условие акции.': 'You can add a phone number in your profile after signing in. Phone confirmation comes later — then brands will be able to require a confirmed number as a condition.',
  'Номер уже в открытом списке участников. Победителей выберет публичный сид в день розыгрыша — результат можно будет пересчитать самому.': 'The number is already in the public participant list. Winners will be chosen by the public seed on draw day — and the result can be recomputed by anyone.',
  'Нужен вход': 'Sign-in required',
  'Нужно войти': 'You need to sign in',
  'Обмен': 'Exchange',
  'Обмен баллов': 'Points exchange',
  'Обновить работу': 'Update the entry',
  'Объявляется внешний публичный сид, которым не управляет ни бренд, ни ALLSWIPES. Победители = HMAC-SHA256(сид сервера, публичный сид ‖ корень ‖ счётчик) с отбраковкой без смещения по модулю.': 'An external public seed is announced, controlled by neither the brand nor ALLSWIPES. Winners = HMAC-SHA256(server seed, public seed ‖ root ‖ counter) with rejection sampling that avoids modulo bias.',
  'Один код принимается один раз.': 'One code is accepted once.',
  'Один раз на человека': 'Once per person',
  'Один человек — один голос за работу. Итоги фиксируются снимком, который нельзя переписать незаметно.': 'One person — one vote per entry. The results are fixed by a snapshot that cannot be rewritten unnoticed.',
  'Он под крышкой, на этикетке или в чеке. Один код — одно участие, повторно его не примут ни у кого.': 'It is under the cap, on the label or in the receipt. One code — one entry, and it will not be accepted twice from anyone.',
  'Описание': 'Description',
  'Опубликовано': 'Published',
  'Опубликовано при запуске': 'Published at launch',
  'Организатор': 'Organiser',
  'Оставить отзыв': 'Leave a review',
  'Осталось собрать {left} из {total} — за полный набор выдаётся «{prize}».': '{left} of {total} left to collect — a full set gets you «{prize}».',
  'Отзыв оставлен': 'Review left',
  'Отзывов пока нет. Их пишут только те, кто подтвердил получение приза, — поэтому их всегда меньше, чем участников.': 'No reviews yet. Only people who confirmed they received a prize write them, so there are always fewer reviews than participants.',
  'Отзывы о призах этой акции': 'Reviews of this campaign\'s prizes',
  'Открыть': 'Open',
  'Открыть верификатор': 'Open the verifier',
  'Открыть цепочку': 'Open the chain',
  'Отлично, приз у тебя': 'Great, the prize is yours',
  'Отличный результат': 'Excellent result',
  'Отметь получение': 'Mark it as received',
  'Отправить': 'Send',
  'Отправить отзыв': 'Send the review',
  'Отправляем…': 'Sending…',
  'Отсканируй и выбери цифру': 'Scan and pick a digit',
  'Отсканируй или введи': 'Scan it or type it',
  'Отсканируй код с упаковки или из чека — и он появится здесь.': 'Scan a code from a pack or a receipt and it will appear here.',
  'Отчёт не найден': 'Report not found',
  'Отчёт о розыгрыше →': 'Draw report →',
  'Оформить в «Мои участия»': 'Claim it in «My entries»',
  'Оформить до {day}': 'Claim by {day}',
  'Оформить приз': 'Claim the prize',
  'Оформление приза': 'Claiming the prize',
  'Оформляем…': 'Claiming…',
  'Оценку ставит только тот, кто подтвердил получение приза, — накрутить рейтинг отзывами со стороны нельзя.': 'Only someone who confirmed receiving a prize can rate it — outside reviews cannot inflate the rating.',
  'Очистить': 'Clear',
  'Очистить поиск': 'Clear the search',
  'Пара слов о тебе': 'A couple of words about you',
  'Партнёр готовит приз — напишем, когда можно забрать.': 'The partner is preparing the prize — we will write when you can collect it.',
  'Партнёр подготовит приз и назовёт точку выдачи.': 'The partner will prepare the prize and name the pickup point.',
  'Передать данные и получить награду': 'Share the data and get the reward',
  'Передать и участвовать': 'Share and take part',
  'Передаётся только то, что в этом списке. Остальная анкета остаётся при тебе, а запись о передаче появится в профиле — «Кому ты передал данные». Отказ ничего не стоит: код останется неиспользованным.': 'Only what is on this list is shared. The rest of your profile stays with you, and a record of the transfer appears in your profile under «Who you shared data with». Refusing costs nothing: the code stays unused.',
  'Пересчитай результат сам': 'Recompute the result yourself',
  'Пересчитанные победители совпадают с опубликованными, а сид — со своим хешем.': 'The recomputed winners match the published ones, and the seed matches its hash.',
  'Пересчитано': 'Recomputed',
  'Пересчитать корень списка': 'Recompute the list root',
  'Пересчитать победителей': 'Recompute the winners',
  'Пересчитать пока нельзя': 'Recomputation is not possible yet',
  'Пересчёт в твоём браузере дал другой результат. Ниже видно, где именно.': 'The recalculation in your browser gave a different result. Below you can see exactly where.',
  'Персональные данные не публикуются — только номер билета, страна и что стало с призом.': 'Personal data is not published — only the ticket number, the country and what happened to the prize.',
  'По этой акции не было ни одного билета — сид всё равно раскрыт, чтобы отсутствие победителей можно было проверить так же, как их наличие.': 'This campaign had no tickets at all — the seed is still revealed, so the absence of winners can be checked the same way their presence would be.',
  'Победителей': 'Winners',
  'Победителей нет': 'No winners',
  'Победители': 'Winners',
  'Подойдёт снимок крышки, этикетки или чека. Картинка не покидает телефон: код читается прямо здесь, на сервер уходит только он сам.': 'A photo of the cap, the label or the receipt will do. The image never leaves your phone: the code is read right here, and only the code goes to the server.',
  'Подтверди оформление': 'Confirm the claim',
  'Подтверждённый номер бренды смогут требовать как условие акции.': 'Brands will be able to require a confirmed number as a condition of a campaign.',
  'Пока вводи код вручную или загрузи фото QR — так работает и код под крышкой, и код из чека.': 'For now type the code by hand or upload a photo of the QR — that works for a code under the cap and for one from a receipt.',
  'Пока нет выданных призов': 'No prizes handed out yet',
  'Пока ни одного участия': 'No entries yet',
  'Пока никого. Отправь ссылку — друг получит {n} {points} сразу после первого участия.': 'Nobody yet. Send the link — your friend gets {n} {points} right after their first entry.',
  'Пока никому. Когда акция попросит данные, ты увидишь список полей и их значения до того, как подтвердишь передачу.': 'Nobody yet. When a campaign asks for data you will see the list of fields and their values before you confirm.',
  'Пока никто не играл': 'Nobody has played yet',
  'Пока пусто': 'Empty for now',
  'Пока тихо': 'Quiet for now',
  'Покажи номер': 'Show the number',
  'Показать ещё': 'Show more',
  'Показываем тот же билет — второй раз код не считается.': 'We show the same ticket — the code does not count a second time.',
  'Показываем тот же результат.': 'We show the same result.',
  'Полные правила акции': 'Full campaign rules',
  'Полный текст правил': 'Full text of the rules',
  'Получи билет': 'Get a ticket',
  'Получи приз сразу': 'Get a prize right away',
  'Получить код': 'Get the code',
  'После': 'Afterwards',
  'После розыгрыша раскрывается сид сервера, а публичный сид — это тираж гослото. Верификатор в твоём браузере считает победителей заново и сравнивает со списком.': 'After the draw the server seed is revealed, and the public seed is a state lottery result. The verifier in your browser recomputes the winners and compares them with the list.',
  'Последние результаты': 'Latest results',
  'Посмотреть условия обмена': 'See the exchange terms',
  'Поставь оценку': 'Give a rating',
  'Посчитано сейчас в твоём браузере': 'Computed just now in your browser',
  'Посчитать хеш': 'Compute the hash',
  'Почта': 'Email',
  'Правила акции': 'Campaign rules',
  'Правильные ответы зафиксированы заранее и одинаковы для всех — квиз нельзя подкрутить под человека. Сверяет их сервер, поэтому до ответа они не приходят в приложение.': 'The correct answers are fixed in advance and the same for everyone — the quiz cannot be tuned per person. The server checks them, so they never reach the app before you answer.',
  'Предыдущее': 'Previous',
  'Приведи друга': 'Refer a friend',
  'Приглашение': 'Invitation',
  'Приглашение не принято': 'Invitation not accepted',
  'Приглашение принято': 'Invitation accepted',
  'Пригодится для связи по призу': 'Useful for getting in touch about the prize',
  'Приз': 'Prize',
  'Приз доступен сразу — смотри в «Мои участия»': 'The prize is available right away — see «My entries»',
  'Приз за полный набор': 'Prize for a full set',
  'Приз за цепочку — {prize}. Оформить его можно в «Моих участиях».': 'The prize for the chain is {prize}. You can claim it in «My entries».',
  'Приз и условия этой акции видят те, кто пришёл по ссылке бренда': 'The prize and terms of this campaign are visible to those who came via the brand\'s link',
  'Приз нужно оформить': 'The prize has to be claimed',
  'Приз появится в приложении сразу.': 'The prize appears in the app right away.',
  'Призов': 'Prizes',
  'Призы выдавались, отзывов пока нет': 'Prizes were handed out, no reviews yet',
  'Призы и шансы': 'Prizes and odds',
  'Призы проверены платформой': 'Prizes verified by the platform',
  'Призы проверены платформой: партнёр приложил доказательство, мы его посмотрели': 'Prizes verified by the platform: the partner attached proof and we looked at it',
  'Прислать работу': 'Submit an entry',
  'Пришли свою — она появится здесь после проверки.': 'Send yours — it appears here after moderation.',
  'Приём работ до': 'Entries accepted until',
  'Пробелы, дефисы и регистр не важны. Один код принимается один раз.': 'Spaces, hyphens and case do not matter. One code is accepted once.',
  'Проверить код': 'Check the code',
  'Проверить розыгрыш': 'Verify the draw',
  'Проверить список билетов': 'Verify the ticket list',
  'Проверить хеш': 'Check the hash',
  'Проверка текста': 'Text check',
  'Проверяем…': 'Checking…',
  'Продолжить': 'Continue',
  'Проект': 'Project',
  'Пройдено {done} из {total}': '{done} of {total} done',
  'Пропустил день —': 'Missed a day —',
  'Прочитано': 'Read',
  'Публичный сид': 'Public seed',
  'Публичный сид — вставь из официального источника': 'Public seed — paste it from the official source',
  'Работ пока нет': 'No entries yet',
  'Работа обновлена — она снова уйдёт на проверку': 'The entry is updated — it goes back to moderation',
  'Работа отправлена на проверку': 'The entry is sent for moderation',
  'Работы участников': 'Entries',
  'Разбор ответов': 'Answer breakdown',
  'Ранг': 'Rank',
  'Раскрыт': 'Revealed',
  'Регион обновлён': 'Region updated',
  'Режим разработки: код {code} подставлен автоматически. В бою он приходит письмом и в ответе API не появляется.': 'Development mode: the code {code} is filled in automatically. In production it arrives by email and never appears in the API response.',
  'Результат': 'Result',
  'Результат не совпал': 'The result did not match',
  'Результаты': 'Results',
  'Розыгрыш': 'Draw',
  'Рядом с номером — цифра участника, страна, пол (если указан), месяц регистрации и время участия. Ни имени, ни города, ни контактов здесь нет и не будет.': 'Next to the number: the participant\'s digit, country, gender (if given), month of registration and time of entry. No name, no city, no contacts here — and there never will be.',
  'Свой экземпляр': 'Your own copy',
  'Сегодня отмечено · вернись завтра за +{n}': 'Checked in today · come back tomorrow for +{n}',
  'Сегодня уже отмечено — награда начислена один раз': 'Already checked in today — the reward is credited once',
  'Сейчас акций нет': 'No campaigns right now',
  'Сервер создаёт случайный сид и публикует его SHA-256. Сид секретный, хеш — нет.': 'The server creates a random seed and publishes its SHA-256. The seed is secret, the hash is not.',
  'Серия из {total} {days} собрана целиком — {earned} {points}.': 'A streak of {total} {days} completed in full — {earned} {points}.',
  'Серия привязана к аккаунту: без входа её некуда записывать.': 'The streak is tied to your account: without signing in there is nowhere to record it.',
  'Сид раскрыт': 'Seed revealed',
  'Сид раскрыт. Пересчитай результат сам — мы ничего не подставляем.': 'The seed is revealed. Recompute the result yourself — we substitute nothing.',
  'Сид сервера': 'Server seed',
  'Сид сервера (раскрыт)': 'Server seed (revealed)',
  'Сид сервера раскрывается только после окончания акции. До этого опубликован его хеш — он доказывает, что сид уже существовал и не может измениться. Если бы сид был открыт сейчас, любой мог бы заранее посчитать, какие коды выигрышные.': 'The server seed is revealed only after the campaign ends. Until then its hash is published — proof that the seed already existed and cannot change. If the seed were open now, anyone could work out in advance which codes win.',
  'Сид сервера раскрывается. Его хеш обязан совпасть с опубликованным до старта — значит, подменить сид было нельзя.': 'The server seed is revealed. Its hash must match the one published before the start — so the seed could not have been swapped.',
  'Сид сервера раскрыт': 'Server seed revealed',
  'Скачаем открытый список участников и посчитаем его корень Меркла заново. Если он совпадёт с опубликованным, значит розыгрыш считался именно по этому списку — и ни один билет не добавили и не убрали после фиксации.': 'We download the public participant list and recompute its Merkle root. If it matches the published one, the draw was computed over exactly this list — and no ticket was added or removed after it was frozen.',
  'Сколько раз можно участвовать': 'How many times you can enter',
  'Скрыть правила': 'Hide the rules',
  'Слайды': 'Slides',
  'Следующее': 'Next',
  'Сменить страну можно только с подтверждённой почтой: страна определяет, в каких акциях ты участвуешь.': 'You can change country only with a confirmed email: the country decides which campaigns you take part in.',
  'Снимок итогов': 'Results snapshot',
  'Собери все {total} — приз «{prize}» выдаётся за полный набор, без розыгрыша.': 'Collect all {total} — the prize «{prize}» is given for a full set, with no draw.',
  'Совпало полностью': 'Matched exactly',
  'Сохранить анкету': 'Save the profile',
  'Сохранить контакты': 'Save contacts',
  'Сохраняем…': 'Saving…',
  'Спасибо за отзыв': 'Thanks for the review',
  'Список билетов зафиксирован раньше, чем объявлен публичный сид. Поэтому ни бренд, ни платформа не могли подогнать ни состав участников, ни случайность.': 'The ticket list was frozen before the public seed was announced. Neither the brand nor the platform could tune the list of participants or the randomness.',
  'Список билетов фиксируется корнем Меркла и публикуется — до того, как станет известен публичный сид.': 'The ticket list is frozen with a Merkle root and published — before the public seed is known.',
  'Список зафиксирован': 'The list is frozen',
  'Список тот же': 'The same list',
  'Список фиксируется до объявления публичного сида — по нему и считаются победители.': 'The list is frozen before the public seed is announced — and the winners are computed from it.',
  'Срок оформления — до {day}.': 'The claim deadline is {day}.',
  'Срок переносился': 'The date was moved',
  'Сроки': 'Dates',
  'Ссылка скопирована': 'Link copied',
  'Ссылка-приглашение привязана к аккаунту — иначе непонятно, кому начислять награду.': 'The invite link is tied to an account — otherwise there is no telling who to credit.',
  'Страна': 'Country',
  'Считаем…': 'Computing…',
  'Твои билеты': 'Your tickets',
  'Твой билет': 'Your ticket',
  'Твой набор': 'Your set',
  'Твой текст': 'Your text',
  'Твоя работа': 'Your entry',
  'Твоя работа: {title} — {state}': 'Your entry: {title} — {state}',
  'Твоя ссылка-приглашение': 'Your invite link',
  'Твоя цепочка': 'Your chain',
  'Твоя цифра складывается с цифрами остальных участников. Из суммы получается множитель, который входит в сид розыгрыша — вместе с публичным тиражом, который объявят после закрытия приёма кодов.': 'Your digit is added to the digits of the other participants. The sum becomes a multiplier that enters the draw seed — together with the public lottery result announced after code redemption closes.',
  'Текст': 'Text',
  'Текст правил': 'Text of the rules',
  'Текст правил недоступен.': 'The text of the rules is unavailable.',
  'Текст правил ниже — ровно тот, что был опубликован при запуске акции. Изменить его после запуска нельзя: это сломало бы хеш, который уже разошёлся по страницам и скриншотам.': 'The rules below are exactly what was published when the campaign launched. They cannot be changed afterwards: that would break the hash already spread across pages and screenshots.',
  'Телеграм': 'Telegram',
  'Телефон': 'Phone',
  'Тема': 'Theme',
  'Транскрипт': 'Transcript',
  'Ты даёшь': 'You give',
  'Ты не вошёл': 'You are not signed in',
  'Ты получаешь': 'You get',
  'У акций с розыгрышем ты выбираешь одну цифру из трёх. Цифры всех участников складываются, из суммы получается множитель — и он входит в сид розыгрыша.': 'In campaigns with a draw you pick one digit out of three. All participants\' digits are added up, the sum becomes a multiplier, and it enters the draw seed.',
  'У бренда нет идущих акций. Загляни позже — или посмотри другие бренды в ленте.': 'This brand has no running campaigns. Look again later — or browse other brands in the feed.',
  'Уникальный код {place}.': 'A unique code {place}.',
  'Условие переноса объявлено в правилах до старта. Каждый перенос записан здесь — с прежней и новой датой.': 'The condition for moving the date was announced in the rules before the start. Every move is recorded here — with the old and the new date.',
  'Условие по ссылке закончилось': 'The link\'s offer has ended',
  'Условие по твоей ссылке': 'The offer from your link',
  'Условия зафиксированы': 'Terms are frozen',
  'Условия открываются по ссылке': 'The terms open via the link',
  'Участников': 'Participants',
  'Файл': 'File',
  'Файл или ссылка': 'File or link',
  'Файл не загрузился': 'The file did not upload',
  'Файл работы': 'Entry file',
  'Фото': 'Photo',
  'Хеш правил': 'Rules hash',
  'Хеш сида': 'Seed hash',
  'Хеш сида опубликован до старта, поэтому результат нельзя изменить задним числом. Сам сид раскроем вместе с итогами розыгрыша.': 'The seed hash is published before the start, so the result cannot be changed after the fact. The seed itself is revealed together with the draw results.',
  'Хеш сида опубликован до старта. Сам сид закрыт до конца акции: иначе можно было бы заранее посчитать, какие коды выигрышные.': 'The seed hash is published before the start. The seed itself stays closed until the campaign ends: otherwise anyone could work out in advance which codes win.',
  'Хеш сида сервера': 'Server seed hash',
  'Хеш сида сходится': 'The seed hash checks out',
  'Хеш считается как SHA-256 от текста правил целиком, байт в байт. Расхождение означает, что текст отличается хотя бы одним символом — об этом стоит спросить организатора по контакту из правил.': 'The hash is SHA-256 over the whole text of the rules, byte for byte. A mismatch means the text differs by at least one character — worth asking the organiser about, using the contact in the rules.',
  'Хеш твоего экземпляра': 'Hash of your copy',
  'Хеш — это отпечаток текста. Одинаковый текст всегда даёт одинаковый отпечаток, а любое изменение — совершенно другой. Поэтому организатор не может переписать условия задним числом: старый отпечаток останется в приложении, на странице акции и у всех, кто его сохранил.': 'A hash is a fingerprint of a text. The same text always gives the same fingerprint, and any change gives a completely different one. So the organiser cannot rewrite the terms after the fact: the old fingerprint stays in the app, on the campaign page and with everyone who saved it.',
  'Цепочка': 'Chain',
  'Цифра попадёт в открытый список участников рядом с номером билета. Любой сможет сложить цифры и получить тот же множитель — в этом и смысл: на результат влияют участники, а не организатор.': 'The digit goes into the public participant list next to the ticket number. Anyone can add the digits up and get the same multiplier — that is the point: participants influence the result, not the organiser.',
  'Часть полей не заполнена — их можно заполнить на экране согласия.': 'Some fields are empty — you can fill them in on the consent screen.',
  'Читаем картинку…': 'Reading the image…',
  'Что понравилось, что нет': 'What you liked and what you did not',
  'Что присылать': 'What to submit',
  'Что это за сделка': 'What the deal is',
  'Что это и как сделано': 'What this is and how it is built',
  'Чтобы приглашение засчиталось другу, нужно войти — награда начисляется на аккаунт.': 'For the invitation to count for your friend you need to sign in — the reward goes to an account.',
  'Чтобы прислать работу и голосовать, нужно войти.': 'To submit an entry and to vote you need to sign in.',
  'Шаги закончились, но цепочка ещё не закрыта — обнови страницу.': 'The steps are done but the chain is not closed yet — refresh the page.',
  'Шанс билета': 'Ticket chance',
  'Шанс одного билета при текущем числе участников — {chance}%. Чем больше участников, тем ниже шанс: мы показываем его честно, а не прячем.': 'One ticket\'s chance at the current number of participants is {chance}%. The more participants, the lower the chance: we show it honestly instead of hiding it.',
  'Эти данные остаются в твоём профиле и никуда не уходят сами. Бренд получит их, только если его акция их запросит и ты это подтвердишь.': 'This data stays in your profile and goes nowhere on its own. A brand receives it only if its campaign asks and you confirm.',
  'Это следующий шаг. Остальные откроются после него.': 'This is the next step. The rest open after it.',
  'Этот браузер не умеет читать QR из картинки. Введи код с упаковки руками — или открой приложение в Chrome.': 'This browser cannot read a QR from an image. Type the code from the pack by hand — or open the app in Chrome.',
  'Язык': 'Language',
  'автомобиль': 'car',
  'балл|балла|баллов': 'point|points',
  'балл': 'point',
  'балла': 'points',
  'баллов': 'points',
  'баллов с цепи': 'points from the chain',
  'баллов собрано': 'points collected',
  'без названия': 'untitled',
  'билет {number}': 'ticket {number}',
  'билеты, розыгрыши и призы — каждый результат можно пересчитать самому': 'tickets, draws and prizes — every result can be recomputed yourself',
  'в ALLSWIPES с {since}': 'on ALLSWIPES since {since}',
  'в акции, а не в момент перехода по ссылке. Самоприглашения и второй пригласивший отклоняются сервером.': 'in a campaign, not at the moment the link is opened. Self-invites and a second referrer are rejected by the server.',
  'в обработке': 'processing',
  'верно': 'correct',
  'вопрос|вопроса|вопросов': 'question|questions',
  'вопрос': 'question',
  'вопроса': 'questions',
  'вопросов': 'questions',
  'все и жюри (голос жюри весит больше)': 'everyone and the jury (a jury vote weighs more)',
  'все, кто вошёл': 'everyone signed in',
  'вход, регион, анкета и согласия — видно, кому и какие данные ушли': 'sign-in, region, profile and consents — you see who received which data',
  'выдаётся в точке партнёра': 'handed out at the partner\'s location',
  'выигрыш': 'win',
  'выполнил условие · +{n} тебе': 'met the condition · +{n} for you',
  'выполнили условие': 'met the condition',
  'голос|голоса|голосов': 'vote|votes',
  'голос': 'vote',
  'голоса': 'votes',
  'голосов': 'votes',
  'голосование': 'voting',
  'город': 'city',
  'готов к выдаче': 'ready for pickup',
  'д': 'd',
  'да': 'yes',
  'данные за ценность — честная сделка': 'data for value — an explicit deal',
  'дата известна заранее и не двигается без причины': 'the date is known in advance and does not move without reason',
  'дату рождения': 'date of birth',
  'детей в семье': 'children in the family',
  'день|дня|дней': 'day|days',
  'дней': 'days',
  'дней отмечено': 'days checked in',
  'дня': 'days',
  'до {day}': 'until {day}',
  'до «{rank}» — {left}': 'to «{rank}» — {left}',
  'до конца акции': 'until the campaign ends',
  'до конца условия': 'until the offer ends',
  'до розыгрыша · 12 призов': 'until the draw · 12 prizes',
  'до старта': 'until the start',
  'доступен сразу': 'available right away',
  'друга': 'friends',
  'друзей': 'friends',
  'друга|друзей|друзей': 'friend|friends',
  'ещё не оформил': 'not claimed yet',
  'ждём розыгрыш': 'waiting for the draw',
  'ждёт очереди': 'waiting in line',
  'жюри платформы': 'the platform jury',
  'за следующий день': 'for the next day',
  'завершён': 'closed',
  'занял место #{pos}': 'took place #{pos}',
  'зап.': 'res.',
  'здесь выигрывает работа, а не билет: присылай свою и голосуй за чужие': 'here the entry wins, not the ticket: send yours and vote for others',
  'знание аудитории, не тебя': 'knowledge of the audience, not of you',
  'и ещё': 'and {n} more',
  'и ещё {n}': 'and {n} more',
  'из {spots} · занято {taken}': 'of {spots} · {taken} taken',
  'или': 'or',
  'имя': 'name',
  'итоги': 'results',
  'итоги подведены {day}': 'results announced {day}',
  'карусель': 'carousel',
  'код под крышкой': 'code under the cap',
  'код под крышкой, на этикетке или в чеке': 'a code under the cap, on the label or in the receipt',
  'копировать': 'copy',
  'кто делал — можно одному': 'who made it — one person is fine',
  'любимый продукт': 'favourite product',
  'мест': 'places',
  'места': 'slots',
  'места закончились': 'no slots left',
  'место': 'place',
  'можно проверить': 'can be verified',
  'на проверке': 'in moderation',
  'на снимке склада видно адрес и накладную — он не для открытого интернета': 'a warehouse photo shows the address and the invoice — it is not for the open internet',
  'на упаковке': 'on the pack',
  'награда получена': 'reward received',
  'не забрал приз': 'did not collect the prize',
  'не забрал — место переиграно (#{pos})': 'did not collect — the place was replayed (#{pos})',
  'не принята': 'not accepted',
  'не совпадает': 'does not match',
  'не угадал': 'wrong guess',
  'новая акция': 'new campaign',
  'новое': 'new',
  'новые акции': 'new campaigns',
  'новых акций': 'new campaigns',
  'новая акция|новые акции|новых акций': 'new campaign|new campaigns',
  'нужно заполнить': 'needs filling in',
  'нужно оформить': 'needs claiming',
  'одна работа на человека, до публикации её смотрит модератор': 'one entry per person, a moderator reviews it before publication',
  'ожидается': 'pending',
  'опубликована': 'published',
  'осталось {left} из {cap} мест': '{left} of {cap} places left',
  'отзыв|отзыва|отзывов': 'review|reviews',
  'отзыв': 'review',
  'отзыва': 'reviews',
  'отзывов': 'reviews',
  'открыть снимок': 'open the photo',
  'отличается': 'differs',
  'отсчёт начнётся после входа': 'the countdown starts after you sign in',
  'оформляется': 'being processed',
  'пересчитай результат сам, в своём браузере': 'recompute the result yourself, in your browser',
  'перешли по ссылке': 'opened the link',
  'перешёл, ждём участия': 'opened it, waiting for an entry',
  'подтверждение — скоро': 'confirmation — soon',
  'пол': 'gender',
  'получен': 'received',
  'попыток': 'attempts',
  'почта не подтверждена': 'email not confirmed',
  'почта подтверждена': 'email confirmed',
  'почту': 'email',
  'правила акции открыты всем': 'the campaign rules are open to everyone',
  'приз готов к выдаче': 'the prize is ready for pickup',
  'приз получен': 'the prize was received',
  'приз|приза|призов': 'prize|prizes',
  'приём кодов закрыт, список билетов заморожен': 'code redemption is closed, the ticket list is frozen',
  'приём работ': 'accepting entries',
  'проведён': 'completed',
  'проверен платформой': 'verified by the platform',
  'работ': 'entries',
  'работа': 'entry',
  'работы': 'entries',
  'работа|работы|работ': 'entry|entries',
  'реально поучаствовал': 'actually took part',
  'розыгрыш {when}': 'draw {when}',
  'с баннера, флаера или от блогера': 'from a banner, a flyer or a blogger',
  'свободно {n}': '{n} free',
  'сектор': 'sector',
  'сектора': 'sectors',
  'секторов': 'sectors',
  'сектор|сектора|секторов': 'sector|sectors',
  'серия не обнуляется в наказание': 'the streak is not reset as a punishment',
  'сид ещё закрыт': 'the seed is still closed',
  'сид закрыт': 'seed closed',
  'сид опубликован — результат пересчитывается в браузере': 'the seed is published — the result is recomputed in the browser',
  'сид раскрыт': 'seed revealed',
  'скоро': 'soon',
  'совпадает': 'matches',
  'совпадает с опубликованным': 'matches the published one',
  'срок оформления вышел': 'the claim deadline has passed',
  'старт {day}': 'starts {day}',
  'считаем…': 'computing…',
  'твой выбор влияет на результат': 'your choice affects the result',
  'твой голос': 'your vote',
  'твоя работа': 'your entry',
  'твоё время по этой ссылке': 'your time on this link',
  'твоё место': 'your place',
  'ты': 'you',
  'ты уже воспользовался': 'you have already used it',
  'уже заполнено': 'already filled in',
  'файл не открывается — посмотреть по ссылке': 'the file will not open — view it by link',
  'хеш не опубликован': 'the hash is not published',
  'цепочка пройдена': 'chain completed',
  'шаги проходятся по порядку — награда за каждый начисляется сразу': 'steps are taken in order — the reward for each is credited immediately',
  'шанс одного билета — {chance}%': 'one ticket\'s chance — {chance}%',
  'этот код уже был': 'this code was already used',
  '— не заполнено': '— not filled in',
  'билет|билета|билетов': 'ticket|tickets',
  'участник|участника|участников': 'participant|participants',
  'акция|акции|акций': 'campaign|campaigns',
  'бренд|бренда|брендов': 'brand|brands',
  'страна|страны|стран': 'country|countries',
  'Новичок': 'Newcomer',
  'Собиратель': 'Collector',
  'Охотник': 'Hunter',
  'Коллекционер': 'Curator',
  'Легенда': 'Legend',
  'Центр': 'Centre',
  'Около центра': 'Near the centre',
  'Середина': 'Middle',
  'У края': 'Near the edge',
  'Край · джекпот': 'Edge · jackpot',
  'начисляются за каждый принятый код — баланс всегда равен сумме истории': 'credited for every accepted code — the balance always equals the sum of the history',
  'США': 'United States',
  'Великобритания': 'United Kingdom',
  'Германия': 'Germany',
  'Франция': 'France',
  'Испания': 'Spain',
  'Япония': 'Japan',
  'Южная Корея': 'South Korea',
  'Сингапур': 'Singapore',
  'ОАЭ': 'UAE',
  'Саудовская Аравия': 'Saudi Arabia',
  'Катар': 'Qatar',
  'Египет': 'Egypt',
  'Область': 'Region',
  'не указана': 'not set',
  'Область обновлена': 'Region updated',
  'Акции, объявленные по штатам и областям, покажутся те, что идут у тебя. Остальные идут по всей стране.': 'Campaigns announced for specific states or regions will show if they run where you are. The rest run nationwide.',
  'Это твой бренд?': 'Is this your brand?',

  // Шаги «как участвовать» — свои у каждой механики, плюс блок честности у
  // механик без случайности и знакомство после первого входа. Общий текст
  // про код врал половине акций: у оффера, анкеты и приза на упаковке
  // кода нет.
  'Открой акцию': 'Open the campaign',
  'Покупка и код не нужны.': 'No purchase, no code.',
  'Нажми «Получить»': 'Tap “Get it”',
  'Предложение откроется сразу, по ссылке.': 'The offer opens right away, as a link.',
  'Пользуйся': 'Use it',
  'Всего предложений: {n}, по одному на человека.': '{n} offers in total, one per person.',
  'Количество не ограничено.': 'No limit on the number.',
  'Купи товар': 'Buy the product',
  'Приз напечатан {place} — производителем, до покупки.': 'The prize is printed {place} — by the maker, before you buy.',
  'Посмотри, что выпало': 'See what you got',
  'Платформа исход не выбирает: он уже на упаковке.': 'The platform picks nothing: the outcome is already on the pack.',
  'Приди с упаковкой': 'Come with the pack',
  'В точку из списка ниже. Сколько призов выдано — видно там же.': 'To a point from the list below. How many prizes are handed out is shown there too.',
  'Посмотри, что просят': 'See what is asked',
  'Список полей и твои значения — до передачи.': 'The list of fields and your own values — before you share them.',
  'Реши сам': 'Decide for yourself',
  'Откажешься — ничего не произойдёт. Награда даётся один раз.': 'Say no and nothing happens. The reward is given once.',
  'Открой квиз': 'Open the quiz',
  'Ответь на вопросы': 'Answer the questions',
  'Правильные ответы открываются сразу.': 'The right answers are revealed at once.',
  'Получи баллы': 'Get the points',
  'Начисляются за попытку, одну на человека.': 'Awarded for one attempt per person.',
  'Отмечайся каждый день': 'Check in every day',
  'Пропуск обнуляет серию.': 'A missed day resets the streak.',
  'Получи приз': 'Get the prize',
  'Когда серия дойдёт до конца.': 'Once the streak is complete.',
  'Возьми свою ссылку': 'Take your link',
  'Она привязана к тебе и видна в акции.': 'It belongs to you and is shown in the campaign.',
  'Позови друга': 'Invite a friend',
  'Баллы начислятся, когда он поучаствует.': 'The points land once they take part.',
  'И ты, и друг — суммы объявлены заранее.': 'Both you and your friend — the amounts are announced up front.',
  'Открой цепочку': 'Open the chain',
  'Шаги видны целиком до начала.': 'Every step is visible before you start.',
  'Проходи шаги по порядку': 'Take the steps in order',
  'Пропустить нельзя, награда за шаг — сразу.': 'Skipping is not allowed; each step pays out at once.',
  'Забери приз': 'Claim the prize',
  'За всю цепочку, после последнего шага.': 'For the whole chain, after the last step.',
  'Внеси вклад': 'Add your part',
  'Нужно {n} участий — счётчик общий и открытый.': '{n} entries are needed — the counter is shared and open.',
  'Счётчик общий и открытый.': 'The counter is shared and open.',
  'Получите все': 'Everyone gets it',
  'Приз достаётся каждому участнику, включая первых.': 'The prize goes to every participant, the first ones included.',
  'Участвуй раньше': 'Take part early',
  'Ступень скидки зависит от номера участия.': 'The discount tier depends on your entry number.',
  'Получи цену': 'Get the price',
  'Когда ступени кончатся, код не потратится.': 'When the tiers run out, your code is not spent.',
  'без случайности': 'no randomness',
  'Платформа ничего не разыгрывает: результат определён условиями, объявленными до старта. Сида в этой механике нет, и его хеш ничего бы не доказывал.': 'The platform draws nothing: the result follows the conditions announced before the start. This mechanic has no seed, and its hash would prove nothing.',
  'Проверяется другое: счётчик считается из тех же строк, по которым начисляется награда, а хеш правил не даёт переписать условия задним числом.': 'Something else is checkable here: the counter is computed from the very rows the rewards are paid from, and the rules hash stops the terms from being rewritten after the fact.',
  'Женский': 'Female',
  'Мужской': 'Male',
  'Не указываю': 'Prefer not to say',
  'Выбери': 'Choose',
  'Это нужно при выдаче приза: как к тебе обратиться и как записано в документе. Остальное бренд спросит сам, если его акции это нужно, — и ты увидишь, что именно передаёшь.': 'This is needed when a prize is handed over: how to address you and how your name is written in your ID. The rest a brand asks for itself, if its campaign needs it — and you will see exactly what you are sharing.',
};

/**
 * Испанский.
 *
 * Ключ тот же — русская фраза. Пропущенная строка берётся из английского
 * словаря: см. translate(). Множественное число устроено как в английском:
 * две формы через «|», выбирает единица.
 */
const es: Dict = {
  'Акции': 'Campañas',
  'Эфиры': 'Sorteos en directo',
  'Мои участия': 'Mis participaciones',
  'Мои': 'Mías',
  'Баллы': 'Puntos',
  'Профиль': 'Perfil',
  'Скан': 'Escanear',
  'Сканировать': 'Escanear código',
  'О проекте': 'Sobre el proyecto',
  'Как это работает': 'Cómo funciona',
  'Войти': 'Entrar',
  'Уведомления': 'Notificaciones',
  'Поиск по акциям и брендам': 'Busca campañas y marcas',
  'Все страны': 'Todos los países',
  'Все': 'Todas',
  'Идут сейчас': 'En curso',
  'Скоро стартуют': 'Empiezan pronto',
  'Итоги': 'Resultados',
  'Бренды': 'Marcas',
  'Ничего не нашлось': 'No se encontró nada',
  'Пока нет активных акций': 'Aún no hay campañas activas',
  'Ещё': 'Más',
  'По умолчанию': 'Por defecto',
  'Скоро закончатся': 'Terminan pronto',
  'Новые': 'Nuevas',
  'Больше участников': 'Más participantes',
  'Ближайший розыгрыш': 'Próximo sorteo',
  'Срок перенесён': 'Fecha aplazada',
  'На этой неделе': 'Esta semana',
  'Почему этому можно верить': 'Por qué puedes confiar',
  'Любой розыгрыш пересчитывается в браузере': 'Cualquier sorteo se recalcula en tu navegador',
  'Смотреть акцию': 'Ver la campaña',
  'Показать новые': 'Ver las nuevas',
  'Открыть отчёт розыгрыша': 'Abrir el informe del sorteo',
  'Где это видно': 'Dónde se ve',
  'В эфире': 'En directo',
  'Скоро': 'Pronto',
  'Состоялись': 'Celebrados',
  'розыгрыши платформы: что идёт сейчас, что будет и чем закончилось': 'sorteos de la plataforma: lo que hay ahora, lo que viene y cómo terminó',
  'Отчёт о розыгрыше': 'Informe del sorteo',
  'Пересчитать самому': 'Recalcúlalo tú mismo',
  'Участвовать': 'Participar',
  'Розыгрышей пока нет': 'Aún no hay sorteos',
  'Условия': 'Condiciones',
  'Участники': 'Participantes',
  'Правила': 'Bases',
  'Честность': 'Juego limpio',
  'Призы': 'Premios',
  'Отсканировать код': 'Escanear un código',
  'Пройти квиз': 'Hacer el test',
  'Отметить день': 'Marcar el día',
  'Пригласить друга': 'Invitar a un amigo',
  'Напомнить': 'Avisarme',
  'Напомним': 'Te avisaremos',
  'Призы проверены платформой.': 'Premios verificados por la plataforma.',
  'участник': 'participante',
  'участника': 'participantes',
  'участников': 'participantes',
  'билет': 'boleto',
  'билета': 'boletos',
  'билетов': 'boletos',
  'приз': 'premio',
  'приза': 'premios',
  'призов': 'premios',
  'идёт': 'en curso',
  'Не загрузилось': 'No se cargó',
  'Повторить': 'Reintentar',
  'Отмена': 'Cancelar',
  'Понятно': 'Entendido',
  'Далее': 'Siguiente',
  'Архив': 'Archivo',
  'Конкурсы': 'Concursos',
  'Платформа, которая создаёт доверие': 'Una plataforma que genera confianza',
  'Промо-акции брендов, результат которых можно пересчитать самому: победителей определяет формула от публичного сида, а не обещание организатора.': 'Campañas promocionales cuyo resultado puedes recalcular tú mismo: a los ganadores los decide una fórmula sobre una semilla pública, no la palabra del organizador.',
  'акция': 'campaña',
  'акции': 'campañas',
  'акций': 'campañas',
  'бренд': 'marca',
  'бренда': 'marcas',
  'брендов': 'marcas',
  'страна': 'país',
  'страны': 'países',
  'стран': 'países',
  'Попробуй другую сферу или страну — или сбрось фильтры и посмотри всё.': 'Prueba otra categoría o país, o quita los filtros y míralo todo.',
  'Как только бренд запустит акцию в твоём регионе, она появится здесь.': 'En cuanto una marca lance una campaña en tu país, aparecerá aquí.',
  'Вопросы и ответы': 'Preguntas y respuestas',
  'коротко и по делу': 'breve y al grano',
  'Как участвовать?': '¿Cómo participo?',
  'Найди код: под крышкой, на этикетке, в чеке или на полке — где именно, написано на странице акции. Отсканируй его в приложении: за розыгрыш ты получишь билет с номером, в мгновенной механике — результат сразу. Один код участвует один раз.': 'Encuentra el código: bajo el tapón, en la etiqueta, en el tíquet o en el estante; la página de la campaña dice exactamente dónde. Escanéalo en la app: en un sorteo recibes un boleto numerado, en una mecánica instantánea el resultado llega al momento. Cada código vale una sola vez.',
  'Откуда мне знать, что розыгрыш честный?': '¿Cómo sé que el sorteo es limpio?',
  'Результат считает формула, а не человек. До розыгрыша публикуется список билетов, свёрнутый в хеш, и обещание сида; в день розыгрыша сид раскрывается — это публичный внешний источник, например тираж гослото, который организатор не выбирает. Дальше победители получаются пересчётом, и этот пересчёт можно повторить самому, прямо в браузере.': 'El resultado lo calcula una fórmula, no una persona. Antes del sorteo se publica la lista de boletos resumida en un hash y el compromiso de la semilla; el día del sorteo la semilla se revela: es una fuente externa pública, como el resultado de una lotería estatal, que el organizador no elige. Los ganadores salen del cálculo, y ese cálculo puedes repetirlo tú mismo, en tu navegador.',
  'Когда и где я получу приз?': '¿Cuándo y dónde recibo el premio?',
  'Мгновенные призы и баллы приходят сразу. Вещевой приз нужно оформить — на это даётся срок, он указан в правилах акции; дальше бренд готовит приз и выдаёт его в своей точке. Статус виден в «Моих участиях» на каждом шаге: оформлен, готов, получен.': 'Los premios instantáneos y los puntos llegan al momento. Un premio físico hay que reclamarlo dentro del plazo indicado en las bases; después la marca lo prepara y lo entrega en su punto. El estado se ve en «Mis participaciones» en cada paso: reclamado, listo, recibido.',
  'Выдаёт приз бренд — он же организатор акции. Платформа следит за сроками и показывает, кто и что обещал.': 'Quien entrega el premio es la marca, que además organiza la campaña. La plataforma vigila los plazos y muestra quién prometió qué.',
  'Это лотерея? Надо что-то платить?': '¿Es una lotería? ¿Hay que pagar algo?',
  'Нет и нет. Это рекламные акции брендов: участие бесплатное, билет нельзя купить, а ставок здесь не бывает. Платформа не берёт денег с участников — за проведение акции платит бренд.': 'No y no. Son campañas publicitarias de marcas: participar es gratis, los boletos no se compran y aquí no hay apuestas. La plataforma no cobra a los participantes: quien paga la campaña es la marca.',
  'Что будет с моими данными?': '¿Qué pasa con mis datos?',
  'Почта нужна для входа — её брендам не передают. Анкету бренд может запросить, но только из закрытого списка полей и только с твоего подтверждения: ты видишь значения до передачи и можешь отказаться, код при этом не тратится. Кому и что ты передал, видно в профиле.': 'El correo sirve para entrar y nunca se da a las marcas. Una marca puede pedir datos del perfil, pero solo de una lista cerrada y solo con tu confirmación: ves los valores antes de enviarlos y puedes negarte sin gastar el código. En tu perfil queda registrado qué compartiste y con quién.',
  'Мои согласия': 'Mis consentimientos',
  'Политика данных': 'Política de datos',
  'Новости и акции': 'Novedades y campañas',
  'Готово': 'Listo',
  'Адрес в списке. Первое письмо придёт, когда появится что-то стоящее: новые акции, итоги розыгрышей, важное по платформе. Отписаться можно одной ссылкой в любом из них.': 'Tu dirección está en la lista. El primer correo llegará cuando haya algo que merezca la pena: campañas nuevas, resultados de sorteos, novedades de la plataforma. Un enlace en cualquiera de ellos cancela la suscripción.',
  'Узнавай об акциях первым': 'Entérate de las campañas el primero',
  'Пишем редко и по делу: что стартовало в твоей стране, где розыгрыш на этой неделе и чем он закончился. Без рекламы ради рекламы — её хватает и без нас.': 'Escribimos poco y al grano: qué empezó en tu país, qué sorteo hay esta semana y cómo terminó. Sin publicidad por publicidad: ya hay bastante sin nosotros.',
  'почта': 'correo',
  'Почта для новостей': 'Correo para las novedades',
  'Минуту…': 'Un momento…',
  'Подписаться': 'Suscribirse',
  'Адрес нужен только для писем об акциях. Брендам он не передаётся — им вообще не видно, кто на что подписан.': 'La dirección solo se usa para los correos sobre campañas. No se entrega a las marcas: ellas no ven quién está suscrito a qué.',
  'Не получилось подписаться': 'No se pudo suscribir',
  'условия открываются по ссылке бренда': 'las condiciones se abren con el enlace de la marca',
  'акции, которые уже прошли: сид опубликован, результат пересчитывается': 'campañas ya terminadas: la semilla está publicada y el resultado se puede recalcular',
  'Архив пока пуст': 'El archivo aún está vacío',
  'Сюда попадают акции, которые закончились. Первая появится здесь, как только завершится хотя бы одна.': 'Aquí llegan las campañas que terminan. La primera aparecerá en cuanto acabe alguna.',
  'В этом промежутке ничего нет': 'No hay nada en este intervalo',
  'Попробуй другую страну или раздвинь даты.': 'Prueba otro país o amplía las fechas.',
  'акций проведено': 'campañas celebradas',
  'призовых мест': 'puestos con premio',
  'Сбросить': 'Limpiar',
  'розыгрыш': 'sorteo',
  'Отчёт розыгрыша': 'Informe del sorteo',
  'Страница акции': 'Página de la campaña',
  'Показано': 'Mostrando',
  'из': 'de',
  'с': 'desde',
  'по': 'hasta',
  'Весь архив открывается после входа — это тот же вход, что для участия, по коду из письма. Страница любой акции и отчёт розыгрыша при этом открыты всем и без него: доказательство прятать нельзя.': 'El archivo completo se abre al entrar, con el mismo acceso que para participar: un código por correo. La página de cualquier campaña y el informe del sorteo siguen abiertos a todos sin entrar: una prueba no se esconde.',
  'Акции по сферам': 'Campañas por categoría',
  'Участнику': 'Para participantes',
  'Проверка': 'Verificación',
  'Магазины': 'Tiendas',
  'Напитки': 'Bebidas',
  'Продукты': 'Alimentación',
  'Кафе': 'Cafeterías',
  'Аптеки': 'Farmacias',
  'Электроника': 'Electrónica',
  'Заправки': 'Gasolineras',
  'Банки': 'Bancos',
  'Туризм': 'Viajes',
  'Авиакомпании': 'Aerolíneas',
  'Подписки': 'Suscripciones',
  'Связь': 'Telecomunicaciones',
  'Все сферы': 'Todas las categorías',
  'Акции и шансы': 'Campañas y probabilidades',
  'Вся лента целиком': 'Todo el feed',
  'Билеты и призы': 'Boletos y premios',
  'Архив акций': 'Archivo de campañas',
  'Что уже прошло': 'Lo que ya terminó',
  'Баллы и ранги': 'Puntos y rangos',
  'За что начисляют': 'Por qué se otorgan',
  'Сканировать код': 'Escanear un código',
  'С упаковки или чека': 'Del envase o del tíquet',
  'Анкета и согласия': 'Perfil y consentimientos',
  'Кому переданы данные': 'Quién recibió tus datos',
  'Как это проверяется': 'Cómo se verifica',
  'Провабли-фейр': 'Provably fair',
  'Что хранится и зачем': 'Qué se guarda y para qué',
  'Кто за что отвечает': 'Quién responde de qué',
  'Что это и кому': 'Qué es y para quién',
  'Кабинет партнёра': 'Consola del socio',
  'Вход для брендов': 'Acceso para marcas',
  'Пилотный статус': 'Proyecto piloto',
  'Чего проект не обещает': 'Lo que el proyecto no promete',
  'Условия использования': 'Condiciones de uso',
  'Политика конфиденциальности': 'Política de privacidad',
  'Акции носят рекламный характер и не являются лотереей. Организатором каждой акции выступает бренд, указанный в её правилах; ALLSWIPES обеспечивает проведение и проверяемость результата. Условия конкретной акции — в её правилах, подписанных хешем, и они не меняются после запуска.': 'Las campañas son publicitarias y no son una lotería. Cada campaña la organiza la marca indicada en sus bases; ALLSWIPES aporta la mecánica y la verificabilidad del resultado. Las condiciones de cada campaña están en sus bases, firmadas con un hash, y no cambian después del lanzamiento.',
  'Платформа прозрачных промо-акций': 'La plataforma de campañas promocionales transparentes',
  'Порядок': 'Orden',
  'Механики': 'Mecánicas',
  'Нашлось': 'Encontradas',
  'По этим условиям ничего нет': 'Nada con estos filtros',
  'до розыгрыша': 'para el sorteo',
  'осталось': 'queda',
  'Участников меньше объявленного минимума — дата сдвинута, условия те же.': 'Hay menos participantes que el mínimo anunciado: la fecha se mueve, las condiciones no.',
  'Посмотреть историю переносов': 'Ver el historial de aplazamientos',
  'Запустились за последние семь дней — коды уже в магазинах.': 'Lanzadas en los últimos siete días: los códigos ya están en las tiendas.',
  'Список билетов и сид публикуются до розыгрыша. Результат проверяется без нас — формулой, а не словом.': 'La lista de boletos y la semilla se publican antes del sorteo. El resultado se verifica sin nosotros: con una fórmula, no con una promesa.',
  'Платформа': 'Plataforma',
  'Сферы': 'Categorías',
  'Страны': 'Países',
  'Гипермаркеты': 'Hipermercados',
  'Супермаркеты': 'Supermercados',
  'Другое': 'Otros',
  'Красота': 'Belleza',
  'Одежда': 'Ropa',
  'Таджикистан': 'Tayikistán',
  'Узбекистан': 'Uzbekistán',
  'Казахстан': 'Kazajistán',
  'Кыргызстан': 'Kirguistán',
  'Туркменистан': 'Turkmenistán',
  'Азербайджан': 'Azerbaiyán',
  'Грузия': 'Georgia',
  'Армения': 'Armenia',
  'Турция': 'Turquía',
  'Монголия': 'Mongolia',
  'Назад': 'Atrás',
  'Сохранить': 'Guardar',
  'Закрыть': 'Cerrar',
  '+{n} {points} · день {day}': '+{n} {points} · día {day}',
  '+{n} баллов': '+{n} puntos',
  '+{n} баллов сверх обычных': '+{n} puntos además de los habituales',
  ': продолжишь с того же места. День считается по календарю акции, поэтому отметиться дважды за сутки нельзя даже с двух устройств.': ': sigues donde lo dejaste. El día se cuenta por el calendario de la campaña, así que no puedes marcar dos veces al día ni desde dos dispositivos.',
  'JPEG, PNG, WebP, MP4 или WebM': 'JPEG, PNG, WebP, MP4 o WebM',
  'https://… или загрузи файл': 'https://… o sube un archivo',
  '{brand} просит эти данные для участия в акции «{campaign}». Перечень полей есть и в правилах акции — пункт 7.': '{brand} pide estos datos para participar en «{campaign}». La lista de campos también está en las bases, apartado 7.',
  '{limit}. Ограничение в правилах акции, пункт 1.6 — оно не появляется в момент отказа.': '{limit}. El límite está en las bases, apartado 1.6: no aparece solo cuando te lo deniegan.',
  '{n} {prizes} выдано': '{n} {prizes} entregados',
  '{n} {tickets} в игре': '{n} {tickets} en juego',
  '{n} баллов': '{n} puntos',
  '{n} баллов за анкету': '{n} puntos por el perfil',
  '{n} дней на оформление': '{n} días para reclamarlo',
  '{n} из 5': '{n} de 5',
  '{n} мин': '{n} min',
  '{n} ответа (обезличенно)': '{n} respuestas (anónimas)',
  '{prize} · выдаётся в точке партнёра': '{prize} · se entrega en el punto del socio',
  '{qn} {questions} — {rn} {points} за участие.': '{qn} {questions}: {rn} {points} por participar.',
  '{shown} из {total}': '{shown} de {total}',
  'Акции создаются в отдельном кабинете партнёра. Доступ выдаёт владелец бренда.': 'Las campañas se crean en la consola del socio. El acceso lo concede el propietario de la marca.',
  'Акции этого бренда приостановлены платформой.': 'La plataforma ha suspendido las campañas de esta marca.',
  'Акция': 'Campaña',
  'Акция не найдена': 'Campaña no encontrada',
  'Акция проведена учебным алгоритмом {algo}. Он небезопасен и больше не используется; пересчёт показан, чтобы исторический результат остался проверяемым.': 'Esta campaña se celebró con el algoritmo de prueba {algo}. No es seguro y ya no se usa; mostramos el recálculo para que el resultado histórico siga siendo verificable.',
  'Алгоритм': 'Algoritmo',
  'Анкета': 'Perfil',
  'Анкета сохранена': 'Perfil guardado',
  'Баланс': 'Saldo',
  'Баллы начисляются за каждый принятый код. Баланс — это сумма всей истории ниже, а не отдельное число: так он не может разойтись с операциями.': 'Se otorgan puntos por cada código aceptado. El saldo es la suma de todo el historial de abajo, no un número aparte: así no puede desviarse de las operaciones.',
  'Без «@»': 'Sin la «@»',
  'Без названия': 'Sin título',
  'Без ограничений по числу участий': 'Sin límite de participaciones',
  'Билет получен': 'Boleto recibido',
  'Билетов': 'Boletos',
  'Билетов зафиксировано': 'Boletos congelados',
  'Браузер не дал посчитать хеш. Открой страницу по https.': 'El navegador no permitió calcular el hash. Abre la página por https.',
  'Бренд': 'Marca',
  'Бренд видит': 'La marca ve',
  'Бренд запрашивает данные': 'La marca pide datos',
  'Бренд не найден': 'Marca no encontrada',
  'Бренд приложил доказательство призового фонда, мы его посмотрели. Это не гарантия выдачи — за неё отвечает организатор, — но обещание больше не висит в воздухе.': 'La marca adjuntó una prueba del fondo de premios y la revisamos. No es una garantía de entrega —de eso responde el organizador—, pero la promesa ya no queda en el aire.',
  'Бренд проверен платформой: мы подтвердили, кто стоит за акциями': 'Marca verificada por la plataforma: confirmamos quién está detrás de las campañas',
  'Бренд просит данные ниже. Ты видишь, что именно уйдёт — передача добровольна, отказ ничего не стоит, а согласие можно отозвать в профиле.': 'La marca pide los datos de abajo. Ves exactamente qué se envía: compartirlos es voluntario, negarte no cuesta nada y el consentimiento se retira desde el perfil.',
  'В день розыгрыша': 'El día del sorteo',
  'В конце приёма кодов': 'Al cerrar la recepción de códigos',
  'В конце цепочки — {prize}.': 'Al final de la cadena: {prize}.',
  'В открытых списках участников виден только номер билета и обезличенная метка — ни почта, ни номер не публикуются никогда.': 'En las listas públicas solo se ve el número de boleto y una etiqueta anónima: ni el correo ni el teléfono se publican nunca.',
  'В публичных списках': 'En las listas públicas',
  'В розыгрыше номер билета сразу попадает в открытый список. В мгновенных механиках приз приходит в ту же секунду — его посчитал сервер по заранее зафиксированному сиду.': 'En un sorteo el número de boleto entra en la lista pública al instante. En las mecánicas instantáneas el premio llega en el mismo segundo: lo calculó el servidor con una semilla comprometida de antemano.',
  'В точке выдачи назови номер билета и подтверди личность.': 'En el punto de entrega di tu número de boleto y acredita tu identidad.',
  'Важное': 'Destacado',
  'Верификатор': 'Verificador',
  'Вернуться к акции': 'Volver a la campaña',
  'Вероятности открыты и не меняются во время акции.': 'Las probabilidades son públicas y no cambian durante la campaña.',
  'Видео': 'Vídeo',
  'Видео акции': 'Vídeo de la campaña',
  'Витрина обмена — этап 3 в плане переноса. Пока баллы копятся и видны бренду как показатель вовлечения.': 'El canje es la etapa 3 de la hoja de ruta. Por ahora los puntos se acumulan y la marca los ve como indicador de participación.',
  'Возможно, акция уже закончилась.': 'Puede que la campaña ya haya terminado.',
  'Возможно, ссылка устарела.': 'Puede que el enlace esté caducado.',
  'Войди, чтобы видеть баллы': 'Entra para ver tus puntos',
  'Войди, чтобы увидеть свои билеты': 'Entra para ver tus boletos',
  'Войти по почте': 'Entrar por correo',
  'Войти через Telegram · скоро': 'Entrar con Telegram · pronto',
  'Войти, чтобы голосовать': 'Entra para votar',
  'Войти, чтобы пройти': 'Entra para participar',
  'Вопрос {n}': 'Pregunta {n}',
  'Вопрос {n} из {total}': 'Pregunta {n} de {total}',
  'Все акции': 'Todas las campañas',
  'Все дни пройдены': 'Todos los días completados',
  'Все работы': 'Todas las obras',
  'Вставь сохранённый текст правил': 'Pega el texto de las bases que guardaste',
  'Вход': 'Acceso',
  'Вход через Telegram скоро появится. Пока входи по почте.': 'El acceso con Telegram llegará pronto. Por ahora entra con tu correo.',
  'Входим по адресу почты — на него придёт код. Пароль не нужен. Почта же привязывает твои билеты и призы, и без неё нельзя сменить страну.': 'Entras con tu correo: allí llega el código. No hace falta contraseña. El correo también vincula tus boletos y premios, y sin él no puedes cambiar de país.',
  'Входные данные розыгрыша': 'Datos de entrada del sorteo',
  'Выбери вариант': 'Elige una opción',
  'Выбери цифру': 'Elige un dígito',
  'Выдача приза': 'Entrega del premio',
  'Выигрышей': 'Premios ganados',
  'Выйти': 'Salir',
  'Голос не прошёл': 'El voto no se contó',
  'Голос учтён': 'Voto contado',
  'Голоса кончились': 'No te quedan votos',
  'Голоса скрыты до конца голосования: видимый счётчик тянет голоса к лидеру.': 'Los votos están ocultos hasta el final: un contador visible arrastra los votos hacia el líder.',
  'Голосов на человека': 'Votos por persona',
  'Голосование до': 'Votación hasta',
  'Голосовать': 'Votar',
  'Д{n}': 'D{n}',
  'Дата розыгрыша': 'Fecha del sorteo',
  'Два поля, которые нужны при выдаче приза: как к тебе обратиться и как записано в документе. Остальное бренд спросит сам, если его акции это нужно, — и ты увидишь, что именно передаёшь.': 'Dos campos que hacen falta al entregar un premio: cómo dirigirnos a ti y cómo figura en tu documento. Lo demás lo pedirá la marca si su campaña lo necesita, y verás exactamente qué compartes.',
  'Для участия нужно передать: {fields}. Перед передачей ты увидишь сами значения и сможешь отказаться — код при этом не потратится.': 'Para participar hay que compartir: {fields}. Antes de enviarlos verás los valores y podrás negarte sin gastar el código.',
  'До {n} {friends}.': 'Hasta {n} {friends}.',
  'До старта': 'Para el inicio',
  'Доказательство открывается после входа': 'La prueba se abre al entrar',
  'Доказательство призового фонда': 'Prueba del fondo de premios',
  'Другие акции': 'Otras campañas',
  'Ежедневный вход': 'Entrada diaria',
  'Если ты сохранил правила себе, вставь их сюда — браузер посчитает хеш и сравнит с опубликованным. Текст никуда не отправляется.': 'Si guardaste las bases, pégalas aquí: el navegador calculará el hash y lo comparará con el publicado. El texto no se envía a ninguna parte.',
  'Есть куда расти': 'Queda margen',
  'Ещё код': 'Otro código',
  'Жди розыгрыш или получи приз сразу': 'Espera el sorteo o llévate el premio al momento',
  'Забрал приз': 'Premio recogido',
  'Забрать день {day} · +{n}': 'Reclamar el día {day} · +{n}',
  'Забрать: {point}': 'Recoger en: {point}',
  'Загружаем…': 'Cargando…',
  'Загрузить фото QR-кода': 'Subir una foto del código QR',
  'Занято {taken} из {spots}. Места раздаются по очереди — по времени подтверждения кода на сервере, без розыгрыша.': '{taken} de {spots} ocupados. Las plazas van por orden: según la hora en que el servidor confirma el código, sin sorteo.',
  'Запасные': 'Suplentes',
  'Запасные разыграны тем же сидом и в том же проходе, что победители, и опубликованы сразу. Если приз не забрали в срок, место переходит следующему по списку — это не новый розыгрыш, а шаг по уже опубликованному порядку.': 'Los suplentes se sortean con la misma semilla y en la misma pasada que los ganadores, y se publican al instante. Si un premio no se recoge a tiempo, la plaza pasa al siguiente de la lista: no es un sorteo nuevo, sino un paso por el orden ya publicado.',
  'Заполнять не обязательно. Эти поля никуда не уходят сами: бренд получит только то, что ты отдельно подтвердишь при участии в его акции.': 'Rellenarlo es opcional. Estos campos no salen solos: la marca recibirá únicamente lo que confirmes aparte al participar en su campaña.',
  'Заходи каждый день — награда растёт. Срыв не наказывается.': 'Entra cada día y la recompensa crece. Fallar un día no se castiga.',
  'Зачем: {why}': 'Para qué: {why}',
  'Заявка отправлена партнёру': 'Solicitud enviada al socio',
  'Здесь появится расписание: мгновенные механики в эфир не попадают — у них нет даты, к которой можно прийти.': 'Aquí aparecerá el calendario: las mecánicas instantáneas no salen en directo, no tienen una fecha a la que acudir.',
  'И оставь отзыв — он виден бренду в статистике.': 'Y deja una reseña: la marca la ve en sus estadísticas.',
  'Изменить почту': 'Cambiar el correo',
  'Иллюстрация': 'Ilustración',
  'Инстаграм': 'Instagram',
  'История': 'Historial',
  'Источник сида': 'Origen de la semilla',
  'Исход посчитал сервер по сиду, хеш которого опубликован до старта акции. Сам сид раскроется после окончания — тогда результат можно будет пересчитать.': 'El resultado lo calculó el servidor con una semilla cuyo hash se publicó antes del inicio. La semilla se revela al terminar: entonces podrás recalcularlo.',
  'Исход считает сервер по заранее зафиксированному сиду.': 'El resultado lo calcula el servidor con una semilla comprometida de antemano.',
  'К акции': 'A la campaña',
  'К акции →': 'A la campaña →',
  'К акциям': 'A las campañas',
  'Кабинет бренда': 'Consola de la marca',
  'Как всё прошло?': '¿Qué tal fue?',
  'Как оценивают призы': 'Cómo se valoran los premios',
  'Как участвовать': 'Cómo participar',
  'Как это проверить': 'Cómo comprobarlo',
  'Камера появится здесь': 'La cámara aparecerá aquí',
  'Квиз': 'Test',
  'Квиз не найден': 'Test no encontrado',
  'Квиз уже пройден — {n} {points} начислены тогда же. Вторая попытка не даётся.': 'El test ya está hecho: {n} {points} se otorgaron entonces. No hay segundo intento.',
  'Когда платформа объявит конкурс, он появится здесь — с условиями, сроками и работами участников.': 'Cuando la plataforma convoque un concurso, aparecerá aquí con sus bases, sus fechas y las obras de los participantes.',
  'Код акции': 'Código de la campaña',
  'Код из письма': 'Código del correo',
  'Код отправлен на {to}. Введи шесть цифр.': 'Código enviado a {to}. Escribe los seis dígitos.',
  'мгновенно': 'al instante',
  'Где получить': 'Dónde recogerlo',
  'Призы и сколько их осталось': 'Premios y cuántos quedan',
  'осталось {n}': 'quedan {n}',
  'разобрали': 'agotados',
  'выдано {issued} из {count}': '{issued} de {count} entregados',
  'Приз напечатан на упаковке производителем. Платформа его не выбирает — она считает выданное, и это число видно всем.': 'El premio lo imprime el fabricante en el envase. La plataforma no lo elige: cuenta lo entregado, y ese número es público.',
  'Где забрать': 'Dónde recogerlo',
  'Адреса объявлены до старта и входят в правила акции.': 'Las direcciones se publican antes del inicio y forman parte de las bases.',
  'Код действует ещё {left}': 'El código es válido durante {left} más',
  'Срок кода истёк — запроси новый.': 'El código ha caducado: solicita uno nuevo.',
  'Отправить код снова': 'Enviar el código de nuevo',
  'Код привязывается к твоему аккаунту — иначе мы не сможем показать билет и приз.': 'El código se vincula a tu cuenta: si no, no podríamos mostrarte el boleto ni el premio.',
  'Код принят': 'Código aceptado',
  'Код с упаковки или из чека': 'Código del envase o del tíquet',
  'Команда': 'Equipo',
  'Кому ты передал данные': 'A quién le diste tus datos',
  'Конкурс': 'Concurso',
  'Конкурс не найден': 'Concurso no encontrado',
  'Конкурсов сейчас нет': 'Ahora no hay concursos',
  'Контакт': 'Contacto',
  'Контакты': 'Contactos',
  'Контакты сохранены': 'Contactos guardados',
  'Корень не совпал': 'La raíz no coincide',
  'Корень списка билетов': 'Raíz de la lista de boletos',
  'Коротко': 'En breve',
  'Кто голосует': 'Quién vota',
  'Лента покажет акции, которые идут здесь.': 'El feed mostrará las campañas que se celebran aquí.',
  'Лента показывает акции, которые идут в твоей стране.': 'El feed muestra las campañas que se celebran en tu país.',
  'Мест': 'Puestos',
  'Места': 'Plazas',
  'Место определила очередь, а не случай: его получили первые {spots} по времени подтверждения кода на сервере. Счётчик мест публичный — это и есть проверка.': 'La plaza la decidió la cola, no el azar: la consiguieron los primeros {spots} según la hora en que el servidor confirmó el código. El contador de plazas es público: esa es la comprobación.',
  'Можно взять значение с сайта тиража и вставить сюда: пересчёт пойдёт по нему, а не по нашей копии.': 'Puedes tomar el valor de la web del sorteo oficial y pegarlo aquí: el recálculo usará ese, no nuestra copia.',
  'НЕТ': 'NO',
  'На картинке не нашёлся QR-код. Попробуй другое фото или введи код руками.': 'No se encontró ningún código QR en la imagen. Prueba con otra foto o escribe el código a mano.',
  'Набор собран — «{prize}» твой. Приз гарантирован за полный набор, без розыгрыша; оформить его можно в «Мои участия».': 'Colección completa: «{prize}» es tuyo. El premio está garantizado por la colección completa, sin sorteo; recláma­lo en «Mis participaciones».',
  'Набор собран — приз «{prize}» гарантирован, без розыгрыша.': 'Colección completa: el premio «{prize}» está garantizado, sin sorteo.',
  'Награда начисляется, только когда друг': 'La recompensa se otorga solo cuando tu amigo',
  'Награда обоим: +{you} тебе, +{friend} другу.': 'Recompensa para los dos: +{you} para ti, +{friend} para tu amigo.',
  'Название': 'Título',
  'Название команды': 'Nombre del equipo',
  'Найди код': 'Encuentra el código',
  'Напоминание снято': 'Aviso cancelado',
  'Напомним заранее': 'Te avisaremos con tiempo',
  'Напомним о старте акции, о розыгрыше за день и о результате.': 'Te avisaremos del inicio de la campaña, del sorteo un día antes y del resultado.',
  'Начать квиз · +{n}': 'Empezar el test · +{n}',
  'Начислено {n} {points} — за участие, независимо от числа верных.': 'Se otorgan {n} {points} por participar, sin importar cuántas acertaste.',
  'Не получилось': 'No funcionó',
  'Не получилось войти': 'No se pudo entrar',
  'Не получилось отметиться': 'No se pudo marcar el día',
  'Не получилось отправить': 'No se pudo enviar',
  'Не получилось отправить код': 'No se pudo enviar el código',
  'Не получилось отправить ответы': 'No se pudieron enviar las respuestas',
  'Не получилось оформить': 'No se pudo reclamar',
  'Не получилось посчитать хеш': 'No se pudo calcular el hash',
  'Не получилось принять приглашение': 'No se pudo aceptar la invitación',
  'Не получилось прочитать картинку. Введи код руками — это всегда работает.': 'No se pudo leer la imagen. Escribe el código a mano: eso siempre funciona.',
  'Не получилось сохранить': 'No se pudo guardar',
  'Не сейчас': 'Ahora no',
  'Не удалось загрузить данные': 'No se pudieron cargar los datos',
  'Не удалось открыть': 'No se pudo abrir',
  'Не удалось пересчитать': 'No se pudo recalcular',
  'Не удалось проверить код': 'No se pudo verificar el código',
  'Не удалось проверить список': 'No se pudo verificar la lista',
  'Не указано': 'Sin indicar',
  'Не чаще раза в день': 'No más de una vez al día',
  'Не чаще раза в месяц': 'No más de una vez al mes',
  'Не чаще раза в неделю': 'No más de una vez a la semana',
  'Не чаще раза в час': 'No más de una vez por hora',
  'Необязательно': 'Opcional',
  'Неплохо': 'No está mal',
  'Ни одна работа не прошла проверку.': 'Ninguna obra pasó la moderación.',
  'Новые коды не принимаются. Правила и результаты прошедших розыгрышей остаются здесь и проверяются как обычно.': 'No se aceptan códigos nuevos. Las bases y los resultados de los sorteos pasados siguen aquí y se verifican como siempre.',
  'Номер попадёт в открытый список участников и в розыгрыш.': 'El número entrará en la lista pública de participantes y en el sorteo.',
  'Номер телефона можно добавить в профиле после входа. Подтверждение номера появится позже — тогда бренды смогут запрашивать подтверждённый номер как условие акции.': 'Puedes añadir un teléfono en tu perfil después de entrar. La verificación del número llegará más adelante: entonces las marcas podrán exigir un número verificado como condición.',
  'Номер уже в открытом списке участников. Победителей выберет публичный сид в день розыгрыша — результат можно будет пересчитать самому.': 'El número ya está en la lista pública de participantes. A los ganadores los elegirá la semilla pública el día del sorteo, y el resultado podrá recalcularlo cualquiera.',
  'Нужен вход': 'Hace falta entrar',
  'Нужно войти': 'Tienes que entrar',
  'Обмен': 'Canje',
  'Обмен баллов': 'Canje de puntos',
  'Обновить работу': 'Actualizar la obra',
  'Объявляется внешний публичный сид, которым не управляет ни бренд, ни ALLSWIPES. Победители = HMAC-SHA256(сид сервера, публичный сид ‖ корень ‖ счётчик) с отбраковкой без смещения по модулю.': 'Se anuncia una semilla pública externa que no controla ni la marca ni ALLSWIPES. Ganadores = HMAC-SHA256(semilla del servidor, semilla pública ‖ raíz ‖ contador) con muestreo por rechazo, sin sesgo de módulo.',
  'Один код принимается один раз.': 'Cada código se acepta una sola vez.',
  'Один раз на человека': 'Una vez por persona',
  'Один человек — один голос за работу. Итоги фиксируются снимком, который нельзя переписать незаметно.': 'Una persona, un voto por obra. Los resultados se fijan con una instantánea que no puede reescribirse sin que se note.',
  'Он под крышкой, на этикетке или в чеке. Один код — одно участие, повторно его не примут ни у кого.': 'Está bajo el tapón, en la etiqueta o en el tíquet. Un código, una participación: no se acepta dos veces a nadie.',
  'Описание': 'Descripción',
  'Опубликовано': 'Publicado',
  'Опубликовано при запуске': 'Publicado en el lanzamiento',
  'Организатор': 'Organizador',
  'Оставить отзыв': 'Dejar una reseña',
  'Осталось собрать {left} из {total} — за полный набор выдаётся «{prize}».': 'Te faltan {left} de {total}: con la colección completa se entrega «{prize}».',
  'Отзыв оставлен': 'Reseña enviada',
  'Отзывов пока нет. Их пишут только те, кто подтвердил получение приза, — поэтому их всегда меньше, чем участников.': 'Todavía no hay reseñas. Solo las escriben quienes confirmaron haber recibido un premio, por eso siempre son menos que los participantes.',
  'Отзывы о призах этой акции': 'Reseñas de los premios de esta campaña',
  'Открыть': 'Abrir',
  'Открыть верификатор': 'Abrir el verificador',
  'Открыть цепочку': 'Abrir la cadena',
  'Отлично, приз у тебя': 'Enhorabuena, el premio es tuyo',
  'Отличный результат': 'Excelente resultado',
  'Отметь получение': 'Marca que lo recibiste',
  'Отправить': 'Enviar',
  'Отправить отзыв': 'Enviar la reseña',
  'Отправляем…': 'Enviando…',
  'Отсканируй и выбери цифру': 'Escanea y elige un dígito',
  'Отсканируй или введи': 'Escanéalo o escríbelo',
  'Отсканируй код с упаковки или из чека — и он появится здесь.': 'Escanea un código del envase o del tíquet y aparecerá aquí.',
  'Отчёт не найден': 'Informe no encontrado',
  'Отчёт о розыгрыше →': 'Informe del sorteo →',
  'Оформить в «Мои участия»': 'Reclamar en «Mis participaciones»',
  'Оформить до {day}': 'Reclamar antes del {day}',
  'Оформить приз': 'Reclamar el premio',
  'Оформление приза': 'Reclamación del premio',
  'Оформляем…': 'Reclamando…',
  'Оценку ставит только тот, кто подтвердил получение приза, — накрутить рейтинг отзывами со стороны нельзя.': 'Solo puntúa quien confirmó haber recibido un premio: la valoración no se infla con reseñas de fuera.',
  'Очистить': 'Limpiar',
  'Очистить поиск': 'Limpiar la búsqueda',
  'Пара слов о тебе': 'Unas palabras sobre ti',
  'Партнёр готовит приз — напишем, когда можно забрать.': 'El socio está preparando el premio: te escribiremos cuando puedas recogerlo.',
  'Партнёр подготовит приз и назовёт точку выдачи.': 'El socio preparará el premio e indicará el punto de entrega.',
  'Передать данные и получить награду': 'Compartir los datos y recibir la recompensa',
  'Передать и участвовать': 'Compartir y participar',
  'Передаётся только то, что в этом списке. Остальная анкета остаётся при тебе, а запись о передаче появится в профиле — «Кому ты передал данные». Отказ ничего не стоит: код останется неиспользованным.': 'Solo se comparte lo que está en esta lista. El resto del perfil se queda contigo, y en «A quién le diste tus datos» aparecerá el registro del envío. Negarte no cuesta nada: el código queda sin usar.',
  'Пересчитай результат сам': 'Recalcula el resultado tú mismo',
  'Пересчитанные победители совпадают с опубликованными, а сид — со своим хешем.': 'Los ganadores recalculados coinciden con los publicados, y la semilla con su hash.',
  'Пересчитано': 'Recalculado',
  'Пересчитать корень списка': 'Recalcular la raíz de la lista',
  'Пересчитать победителей': 'Recalcular los ganadores',
  'Пересчитать пока нельзя': 'Todavía no se puede recalcular',
  'Пересчёт в твоём браузере дал другой результат. Ниже видно, где именно.': 'El recálculo en tu navegador dio otro resultado. Abajo se ve dónde exactamente.',
  'Персональные данные не публикуются — только номер билета, страна и что стало с призом.': 'Los datos personales no se publican: solo el número de boleto, el país y qué pasó con el premio.',
  'По этой акции не было ни одного билета — сид всё равно раскрыт, чтобы отсутствие победителей можно было проверить так же, как их наличие.': 'Esta campaña no tuvo ni un boleto, y aun así la semilla se revela: la ausencia de ganadores se comprueba igual que su presencia.',
  'Победителей': 'Ganadores',
  'Победителей нет': 'Sin ganadores',
  'Победители': 'Ganadores',
  'Подойдёт снимок крышки, этикетки или чека. Картинка не покидает телефон: код читается прямо здесь, на сервер уходит только он сам.': 'Vale una foto del tapón, la etiqueta o el tíquet. La imagen no sale de tu móvil: el código se lee aquí mismo y al servidor solo va el código.',
  'Подтверди оформление': 'Confirma la reclamación',
  'Подтверждённый номер бренды смогут требовать как условие акции.': 'Las marcas podrán exigir un número verificado como condición de una campaña.',
  'Пока вводи код вручную или загрузи фото QR — так работает и код под крышкой, и код из чека.': 'Por ahora escribe el código a mano o sube una foto del QR: funciona tanto con el código del tapón como con el del tíquet.',
  'Пока нет выданных призов': 'Aún no se han entregado premios',
  'Пока ни одного участия': 'Todavía ninguna participación',
  'Пока никого. Отправь ссылку — друг получит {n} {points} сразу после первого участия.': 'Todavía nadie. Envía el enlace: tu amigo recibirá {n} {points} justo después de su primera participación.',
  'Пока никому. Когда акция попросит данные, ты увидишь список полей и их значения до того, как подтвердишь передачу.': 'Todavía a nadie. Cuando una campaña pida datos, verás la lista de campos y sus valores antes de confirmar.',
  'Пока никто не играл': 'Todavía no ha jugado nadie',
  'Пока пусто': 'De momento, vacío',
  'Пока тихо': 'De momento, tranquilo',
  'Покажи номер': 'Muestra el número',
  'Показать ещё': 'Ver más',
  'Показываем тот же билет — второй раз код не считается.': 'Te mostramos el mismo boleto: el código no cuenta una segunda vez.',
  'Показываем тот же результат.': 'Te mostramos el mismo resultado.',
  'Полные правила акции': 'Bases completas de la campaña',
  'Полный текст правил': 'Texto completo de las bases',
  'Получи билет': 'Consigue un boleto',
  'Получи приз сразу': 'Llévate el premio al momento',
  'Получить код': 'Recibir el código',
  'После': 'Después',
  'После розыгрыша раскрывается сид сервера, а публичный сид — это тираж гослото. Верификатор в твоём браузере считает победителей заново и сравнивает со списком.': 'Tras el sorteo se revela la semilla del servidor, y la semilla pública es el resultado de una lotería estatal. El verificador de tu navegador recalcula los ganadores y los compara con la lista.',
  'Последние результаты': 'Últimos resultados',
  'Посмотреть условия обмена': 'Ver las condiciones del canje',
  'Поставь оценку': 'Pon una puntuación',
  'Посчитано сейчас в твоём браузере': 'Calculado ahora en tu navegador',
  'Посчитать хеш': 'Calcular el hash',
  'Почта': 'Correo',
  'Правила акции': 'Bases de la campaña',
  'Правильные ответы зафиксированы заранее и одинаковы для всех — квиз нельзя подкрутить под человека. Сверяет их сервер, поэтому до ответа они не приходят в приложение.': 'Las respuestas correctas están fijadas de antemano y son iguales para todos: el test no se ajusta a cada persona. Las comprueba el servidor, por eso no llegan a la app antes de que respondas.',
  'Предыдущее': 'Anterior',
  'Приведи друга': 'Trae a un amigo',
  'Приглашение': 'Invitación',
  'Приглашение не принято': 'Invitación no aceptada',
  'Приглашение принято': 'Invitación aceptada',
  'Пригодится для связи по призу': 'Sirve para contactarte por el premio',
  'Приз': 'Premio',
  'Приз доступен сразу — смотри в «Мои участия»': 'El premio está disponible ya: míralo en «Mis participaciones»',
  'Приз за полный набор': 'Premio por la colección completa',
  'Приз за цепочку — {prize}. Оформить его можно в «Моих участиях».': 'El premio por la cadena es {prize}. Puedes reclamarlo en «Mis participaciones».',
  'Приз и условия этой акции видят те, кто пришёл по ссылке бренда': 'El premio y las condiciones de esta campaña los ven quienes llegaron por el enlace de la marca',
  'Приз нужно оформить': 'Hay que reclamar el premio',
  'Приз появится в приложении сразу.': 'El premio aparecerá en la app al momento.',
  'Призов': 'Premios',
  'Призы выдавались, отзывов пока нет': 'Se entregaron premios, pero aún no hay reseñas',
  'Призы и шансы': 'Premios y probabilidades',
  'Призы проверены платформой': 'Premios verificados por la plataforma',
  'Призы проверены платформой: партнёр приложил доказательство, мы его посмотрели': 'Premios verificados por la plataforma: el socio aportó una prueba y la revisamos',
  'Прислать работу': 'Enviar una obra',
  'Пришли свою — она появится здесь после проверки.': 'Envía la tuya: aparecerá aquí tras la moderación.',
  'Приём работ до': 'Admisión de obras hasta',
  'Пробелы, дефисы и регистр не важны. Один код принимается один раз.': 'Espacios, guiones y mayúsculas dan igual. Cada código se acepta una sola vez.',
  'Проверить код': 'Comprobar el código',
  'Проверить розыгрыш': 'Verificar el sorteo',
  'Проверить список билетов': 'Verificar la lista de boletos',
  'Проверить хеш': 'Comprobar el hash',
  'Проверка текста': 'Comprobación del texto',
  'Проверяем…': 'Comprobando…',
  'Продолжить': 'Continuar',
  'Проект': 'Proyecto',
  'Пройдено {done} из {total}': '{done} de {total} completados',
  'Пропустил день —': '¿Fallaste un día? —',
  'Прочитано': 'Leído',
  'Публичный сид': 'Semilla pública',
  'Публичный сид — вставь из официального источника': 'Semilla pública: pégala desde la fuente oficial',
  'Работ пока нет': 'Aún no hay obras',
  'Работа обновлена — она снова уйдёт на проверку': 'Obra actualizada: vuelve a moderación',
  'Работа отправлена на проверку': 'Obra enviada a moderación',
  'Работы участников': 'Obras de los participantes',
  'Разбор ответов': 'Repaso de respuestas',
  'Ранг': 'Rango',
  'Раскрыт': 'Revelada',
  'Регион обновлён': 'País actualizado',
  'Режим разработки: код {code} подставлен автоматически. В бою он приходит письмом и в ответе API не появляется.': 'Modo de desarrollo: el código {code} se rellena solo. En producción llega por correo y nunca aparece en la respuesta de la API.',
  'Результат': 'Resultado',
  'Результат не совпал': 'El resultado no coincide',
  'Результаты': 'Resultados',
  'Розыгрыш': 'Sorteo',
  'Рядом с номером — цифра участника, страна, пол (если указан), месяц регистрации и время участия. Ни имени, ни города, ни контактов здесь нет и не будет.': 'Junto al número van el dígito del participante, el país, el género (si se indicó), el mes de registro y la hora de la participación. Ni nombre, ni ciudad, ni contactos: no los hay ni los habrá.',
  'Свой экземпляр': 'Tu copia',
  'Сегодня отмечено · вернись завтра за +{n}': 'Hoy marcado · vuelve mañana por +{n}',
  'Сегодня уже отмечено — награда начислена один раз': 'Hoy ya está marcado: la recompensa se otorga una vez',
  'Сейчас акций нет': 'Ahora no hay campañas',
  'Сервер создаёт случайный сид и публикует его SHA-256. Сид секретный, хеш — нет.': 'El servidor crea una semilla aleatoria y publica su SHA-256. La semilla es secreta; el hash, no.',
  'Серия из {total} {days} собрана целиком — {earned} {points}.': 'Racha de {total} {days} completada entera: {earned} {points}.',
  'Серия привязана к аккаунту: без входа её некуда записывать.': 'La racha va ligada a tu cuenta: sin entrar no hay dónde anotarla.',
  'Сид раскрыт': 'Semilla revelada',
  'Сид раскрыт. Пересчитай результат сам — мы ничего не подставляем.': 'La semilla está revelada. Recalcula el resultado tú mismo: nosotros no sustituimos nada.',
  'Сид сервера': 'Semilla del servidor',
  'Сид сервера (раскрыт)': 'Semilla del servidor (revelada)',
  'Сид сервера раскрывается только после окончания акции. До этого опубликован его хеш — он доказывает, что сид уже существовал и не может измениться. Если бы сид был открыт сейчас, любой мог бы заранее посчитать, какие коды выигрышные.': 'La semilla del servidor se revela solo al terminar la campaña. Hasta entonces está publicado su hash, que demuestra que la semilla ya existía y no puede cambiar. Si la semilla estuviera abierta ahora, cualquiera podría calcular de antemano qué códigos ganan.',
  'Сид сервера раскрывается. Его хеш обязан совпасть с опубликованным до старта — значит, подменить сид было нельзя.': 'La semilla del servidor se revela. Su hash debe coincidir con el publicado antes del inicio: por eso no se pudo cambiar.',
  'Сид сервера раскрыт': 'Semilla del servidor revelada',
  'Скачаем открытый список участников и посчитаем его корень Меркла заново. Если он совпадёт с опубликованным, значит розыгрыш считался именно по этому списку — и ни один билет не добавили и не убрали после фиксации.': 'Descargamos la lista pública de participantes y recalculamos su raíz de Merkle. Si coincide con la publicada, el sorteo se calculó exactamente con esa lista: no se añadió ni se quitó ningún boleto tras congelarla.',
  'Сколько раз можно участвовать': 'Cuántas veces puedes participar',
  'Скрыть правила': 'Ocultar las bases',
  'Слайды': 'Diapositivas',
  'Следующее': 'Siguiente',
  'Сменить страну можно только с подтверждённой почтой: страна определяет, в каких акциях ты участвуешь.': 'Solo puedes cambiar de país con el correo verificado: el país decide en qué campañas participas.',
  'Снимок итогов': 'Instantánea de resultados',
  'Собери все {total} — приз «{prize}» выдаётся за полный набор, без розыгрыша.': 'Reúne los {total}: el premio «{prize}» se entrega por la colección completa, sin sorteo.',
  'Совпало полностью': 'Coincide del todo',
  'Сохранить анкету': 'Guardar el perfil',
  'Сохранить контакты': 'Guardar los contactos',
  'Сохраняем…': 'Guardando…',
  'Спасибо за отзыв': 'Gracias por la reseña',
  'Список билетов зафиксирован раньше, чем объявлен публичный сид. Поэтому ни бренд, ни платформа не могли подогнать ни состав участников, ни случайность.': 'La lista de boletos se congeló antes de anunciar la semilla pública. Por eso ni la marca ni la plataforma pudieron ajustar quién participa ni el azar.',
  'Список билетов фиксируется корнем Меркла и публикуется — до того, как станет известен публичный сид.': 'La lista de boletos se congela con una raíz de Merkle y se publica antes de que se conozca la semilla pública.',
  'Список зафиксирован': 'Lista congelada',
  'Список тот же': 'La misma lista',
  'Список фиксируется до объявления публичного сида — по нему и считаются победители.': 'La lista se congela antes de anunciar la semilla pública, y con ella se calculan los ganadores.',
  'Срок оформления — до {day}.': 'El plazo para reclamarlo termina el {day}.',
  'Срок переносился': 'La fecha se aplazó',
  'Сроки': 'Fechas',
  'Ссылка скопирована': 'Enlace copiado',
  'Ссылка-приглашение привязана к аккаунту — иначе непонятно, кому начислять награду.': 'El enlace de invitación va ligado a una cuenta: si no, no se sabe a quién dar la recompensa.',
  'Страна': 'País',
  'Считаем…': 'Calculando…',
  'Твои билеты': 'Tus boletos',
  'Твой билет': 'Tu boleto',
  'Твой набор': 'Tu colección',
  'Твой текст': 'Tu texto',
  'Твоя работа': 'Tu obra',
  'Твоя работа: {title} — {state}': 'Tu obra: {title} — {state}',
  'Твоя ссылка-приглашение': 'Tu enlace de invitación',
  'Твоя цепочка': 'Tu cadena',
  'Твоя цифра складывается с цифрами остальных участников. Из суммы получается множитель, который входит в сид розыгрыша — вместе с публичным тиражом, который объявят после закрытия приёма кодов.': 'Tu dígito se suma a los de los demás participantes. De esa suma sale un multiplicador que entra en la semilla del sorteo, junto con el resultado público que se anunciará al cerrar la recepción de códigos.',
  'Текст': 'Texto',
  'Текст правил': 'Texto de las bases',
  'Текст правил недоступен.': 'El texto de las bases no está disponible.',
  'Текст правил ниже — ровно тот, что был опубликован при запуске акции. Изменить его после запуска нельзя: это сломало бы хеш, который уже разошёлся по страницам и скриншотам.': 'El texto de abajo es exactamente el que se publicó al lanzar la campaña. No se puede cambiar después: rompería el hash que ya circula por páginas y capturas.',
  'Телеграм': 'Telegram',
  'Телефон': 'Teléfono',
  'Тема': 'Tema',
  'Транскрипт': 'Transcripción',
  'Ты даёшь': 'Tú das',
  'Ты не вошёл': 'No has entrado',
  'Ты получаешь': 'Tú recibes',
  'У акций с розыгрышем ты выбираешь одну цифру из трёх. Цифры всех участников складываются, из суммы получается множитель — и он входит в сид розыгрыша.': 'En las campañas con sorteo eliges un dígito de tres. Los dígitos de todos se suman, de la suma sale un multiplicador y ese entra en la semilla del sorteo.',
  'У бренда нет идущих акций. Загляни позже — или посмотри другие бренды в ленте.': 'Esta marca no tiene campañas en curso. Vuelve más tarde o mira otras marcas en el feed.',
  'Уникальный код {place}.': 'Un código único {place}.',
  'Условие переноса объявлено в правилах до старта. Каждый перенос записан здесь — с прежней и новой датой.': 'La condición para aplazar se anunció en las bases antes del inicio. Cada aplazamiento queda aquí, con la fecha anterior y la nueva.',
  'Условие по ссылке закончилось': 'La oferta del enlace ha terminado',
  'Условие по твоей ссылке': 'La oferta de tu enlace',
  'Условия зафиксированы': 'Condiciones congeladas',
  'Условия открываются по ссылке': 'Las condiciones se abren con el enlace',
  'Участников': 'Participantes',
  'Файл': 'Archivo',
  'Файл или ссылка': 'Archivo o enlace',
  'Файл не загрузился': 'El archivo no se subió',
  'Файл работы': 'Archivo de la obra',
  'Фото': 'Foto',
  'Хеш правил': 'Hash de las bases',
  'Хеш сида': 'Hash de la semilla',
  'Хеш сида опубликован до старта, поэтому результат нельзя изменить задним числом. Сам сид раскроем вместе с итогами розыгрыша.': 'El hash de la semilla se publica antes del inicio, así que el resultado no se puede cambiar a posteriori. La semilla se revela junto con los resultados del sorteo.',
  'Хеш сида опубликован до старта. Сам сид закрыт до конца акции: иначе можно было бы заранее посчитать, какие коды выигрышные.': 'El hash de la semilla se publica antes del inicio. La semilla queda cerrada hasta el final: si no, se podría calcular de antemano qué códigos ganan.',
  'Хеш сида сервера': 'Hash de la semilla del servidor',
  'Хеш сида сходится': 'El hash de la semilla cuadra',
  'Хеш считается как SHA-256 от текста правил целиком, байт в байт. Расхождение означает, что текст отличается хотя бы одним символом — об этом стоит спросить организатора по контакту из правил.': 'El hash es el SHA-256 de todo el texto de las bases, byte a byte. Una diferencia significa que el texto cambia al menos en un carácter: conviene preguntarle al organizador por el contacto de las bases.',
  'Хеш твоего экземпляра': 'Hash de tu copia',
  'Хеш — это отпечаток текста. Одинаковый текст всегда даёт одинаковый отпечаток, а любое изменение — совершенно другой. Поэтому организатор не может переписать условия задним числом: старый отпечаток останется в приложении, на странице акции и у всех, кто его сохранил.': 'Un hash es la huella de un texto. El mismo texto da siempre la misma huella, y cualquier cambio da otra completamente distinta. Por eso el organizador no puede reescribir las condiciones a posteriori: la huella antigua queda en la app, en la página de la campaña y en manos de todos los que la guardaron.',
  'Цепочка': 'Cadena',
  'Цифра попадёт в открытый список участников рядом с номером билета. Любой сможет сложить цифры и получить тот же множитель — в этом и смысл: на результат влияют участники, а не организатор.': 'El dígito irá a la lista pública junto al número de boleto. Cualquiera puede sumar los dígitos y obtener el mismo multiplicador: de eso se trata, en el resultado influyen los participantes y no el organizador.',
  'Часть полей не заполнена — их можно заполнить на экране согласия.': 'Algunos campos están vacíos: puedes rellenarlos en la pantalla de consentimiento.',
  'Читаем картинку…': 'Leyendo la imagen…',
  'Что понравилось, что нет': 'Qué te gustó y qué no',
  'Что присылать': 'Qué enviar',
  'Что это за сделка': 'En qué consiste el trato',
  'Что это и как сделано': 'Qué es y cómo está hecho',
  'Чтобы приглашение засчиталось другу, нужно войти — награда начисляется на аккаунт.': 'Para que la invitación le cuente a tu amigo hay que entrar: la recompensa va a una cuenta.',
  'Чтобы прислать работу и голосовать, нужно войти.': 'Para enviar una obra y votar hay que entrar.',
  'Шаги закончились, но цепочка ещё не закрыта — обнови страницу.': 'Los pasos han terminado pero la cadena sigue abierta: recarga la página.',
  'Шанс билета': 'Probabilidad del boleto',
  'Шанс одного билета при текущем числе участников — {chance}%. Чем больше участников, тем ниже шанс: мы показываем его честно, а не прячем.': 'La probabilidad de un boleto con los participantes actuales es del {chance}%. Cuantos más participantes, menor es: la mostramos con honestidad en vez de esconderla.',
  'Эти данные остаются в твоём профиле и никуда не уходят сами. Бренд получит их, только если его акция их запросит и ты это подтвердишь.': 'Estos datos se quedan en tu perfil y no salen solos. Una marca los recibirá únicamente si su campaña los pide y tú lo confirmas.',
  'Это следующий шаг. Остальные откроются после него.': 'Este es el siguiente paso. Los demás se abren después.',
  'Этот браузер не умеет читать QR из картинки. Введи код с упаковки руками — или открой приложение в Chrome.': 'Este navegador no sabe leer un QR desde una imagen. Escribe el código del envase a mano o abre la app en Chrome.',
  'Язык': 'Idioma',
  'автомобиль': 'coche',
  'балл|балла|баллов': 'punto|puntos',
  'балл': 'punto',
  'балла': 'puntos',
  'баллов': 'puntos',
  'баллов с цепи': 'puntos de la cadena',
  'баллов собрано': 'puntos reunidos',
  'без названия': 'sin título',
  'билет {number}': 'boleto {number}',
  'билеты, розыгрыши и призы — каждый результат можно пересчитать самому': 'boletos, sorteos y premios: cada resultado puedes recalcularlo tú mismo',
  'в ALLSWIPES с {since}': 'en ALLSWIPES desde {since}',
  'в акции, а не в момент перехода по ссылке. Самоприглашения и второй пригласивший отклоняются сервером.': 'en una campaña, no al abrir el enlace. El servidor rechaza las autoinvitaciones y los segundos invitadores.',
  'в обработке': 'en proceso',
  'верно': 'correctas',
  'вопрос|вопроса|вопросов': 'pregunta|preguntas',
  'вопрос': 'pregunta',
  'вопроса': 'preguntas',
  'вопросов': 'preguntas',
  'все и жюри (голос жюри весит больше)': 'todos y el jurado (el voto del jurado pesa más)',
  'все, кто вошёл': 'todos los que han entrado',
  'вход, регион, анкета и согласия — видно, кому и какие данные ушли': 'acceso, país, perfil y consentimientos: se ve quién recibió qué datos',
  'выдаётся в точке партнёра': 'se entrega en el punto del socio',
  'выигрыш': 'premio',
  'выполнил условие · +{n} тебе': 'cumplió la condición · +{n} para ti',
  'выполнили условие': 'cumplieron la condición',
  'голос|голоса|голосов': 'voto|votos',
  'голос': 'voto',
  'голоса': 'votos',
  'голосов': 'votos',
  'голосование': 'votación',
  'город': 'ciudad',
  'готов к выдаче': 'listo para recoger',
  'д': 'd',
  'да': 'sí',
  'данные за ценность — честная сделка': 'datos por valor: un trato claro',
  'дата известна заранее и не двигается без причины': 'la fecha se sabe de antemano y no se mueve sin motivo',
  'дату рождения': 'fecha de nacimiento',
  'детей в семье': 'hijos en la familia',
  'день|дня|дней': 'día|días',
  'дней': 'días',
  'дней отмечено': 'días marcados',
  'дня': 'días',
  'до {day}': 'hasta el {day}',
  'до «{rank}» — {left}': 'para «{rank}»: {left}',
  'до конца акции': 'para que acabe la campaña',
  'до конца условия': 'para que acabe la oferta',
  'до розыгрыша · 12 призов': 'para el sorteo · 12 premios',
  'до старта': 'para el inicio',
  'доступен сразу': 'disponible al momento',
  'друга': 'amigos',
  'друзей': 'amigos',
  'друга|друзей|друзей': 'amigo|amigos',
  'ещё не оформил': 'aún sin reclamar',
  'ждём розыгрыш': 'esperando el sorteo',
  'ждёт очереди': 'espera su turno',
  'жюри платформы': 'el jurado de la plataforma',
  'за следующий день': 'por el día siguiente',
  'завершён': 'cerrado',
  'занял место #{pos}': 'ocupó el puesto #{pos}',
  'зап.': 'supl.',
  'здесь выигрывает работа, а не билет: присылай свою и голосуй за чужие': 'aquí gana la obra, no el boleto: envía la tuya y vota las demás',
  'знание аудитории, не тебя': 'conocer al público, no a ti',
  'и ещё': 'y {n} más',
  'и ещё {n}': 'y {n} más',
  'из {spots} · занято {taken}': 'de {spots} · {taken} ocupadas',
  'или': 'o',
  'имя': 'nombre',
  'итоги': 'resultados',
  'итоги подведены {day}': 'resultados del {day}',
  'карусель': 'carrusel',
  'код под крышкой': 'código bajo el tapón',
  'код под крышкой, на этикетке или в чеке': 'un código bajo el tapón, en la etiqueta o en el tíquet',
  'копировать': 'copiar',
  'кто делал — можно одному': 'quién la hizo; puede ser una sola persona',
  'любимый продукт': 'producto favorito',
  'мест': 'puestos',
  'места': 'plazas',
  'места закончились': 'no quedan plazas',
  'место': 'puesto',
  'можно проверить': 'se puede verificar',
  'на проверке': 'en moderación',
  'на снимке склада видно адрес и накладную — он не для открытого интернета': 'en la foto del almacén se ven la dirección y el albarán: no es para internet abierto',
  'на упаковке': 'en el envase',
  'награда получена': 'recompensa recibida',
  'не забрал приз': 'no recogió el premio',
  'не забрал — место переиграно (#{pos})': 'no lo recogió: la plaza se reasignó (#{pos})',
  'не принята': 'no aceptada',
  'не совпадает': 'no coincide',
  'не угадал': 'fallaste',
  'новая акция': 'campaña nueva',
  'новое': 'nuevo',
  'новые акции': 'campañas nuevas',
  'новых акций': 'campañas nuevas',
  'новая акция|новые акции|новых акций': 'campaña nueva|campañas nuevas',
  'нужно заполнить': 'falta rellenarlo',
  'нужно оформить': 'hay que reclamarlo',
  'одна работа на человека, до публикации её смотрит модератор': 'una obra por persona; antes de publicarse la revisa un moderador',
  'ожидается': 'pendiente',
  'опубликована': 'publicada',
  'осталось {left} из {cap} мест': 'quedan {left} de {cap} plazas',
  'отзыв|отзыва|отзывов': 'reseña|reseñas',
  'отзыв': 'reseña',
  'отзыва': 'reseñas',
  'отзывов': 'reseñas',
  'открыть снимок': 'abrir la foto',
  'отличается': 'difiere',
  'отсчёт начнётся после входа': 'la cuenta atrás empieza al entrar',
  'оформляется': 'en trámite',
  'пересчитай результат сам, в своём браузере': 'recalcula el resultado tú mismo, en tu navegador',
  'перешли по ссылке': 'abrieron el enlace',
  'перешёл, ждём участия': 'lo abrió, esperamos su participación',
  'подтверждение — скоро': 'verificación: pronto',
  'пол': 'género',
  'получен': 'recibido',
  'попыток': 'intentos',
  'почта не подтверждена': 'correo sin verificar',
  'почта подтверждена': 'correo verificado',
  'почту': 'correo',
  'правила акции открыты всем': 'las bases de la campaña están abiertas a todos',
  'приз готов к выдаче': 'el premio está listo para recoger',
  'приз получен': 'premio recibido',
  'приз|приза|призов': 'premio|premios',
  'приём кодов закрыт, список билетов заморожен': 'recepción de códigos cerrada, lista de boletos congelada',
  'приём работ': 'admisión de obras',
  'проведён': 'celebrado',
  'проверен платформой': 'verificada por la plataforma',
  'работ': 'obras',
  'работа': 'obra',
  'работы': 'obras',
  'работа|работы|работ': 'obra|obras',
  'реально поучаствовал': 'participa de verdad',
  'розыгрыш {when}': 'sorteo {when}',
  'с баннера, флаера или от блогера': 'desde un banner, un flyer o un bloguero',
  'свободно {n}': '{n} libres',
  'сектор': 'sector',
  'сектора': 'sectores',
  'секторов': 'sectores',
  'сектор|сектора|секторов': 'sector|sectores',
  'серия не обнуляется в наказание': 'la racha no se reinicia como castigo',
  'сид ещё закрыт': 'la semilla sigue cerrada',
  'сид закрыт': 'semilla cerrada',
  'сид опубликован — результат пересчитывается в браузере': 'la semilla está publicada: el resultado se recalcula en el navegador',
  'сид раскрыт': 'semilla revelada',
  'скоро': 'pronto',
  'совпадает': 'coincide',
  'совпадает с опубликованным': 'coincide con el publicado',
  'срок оформления вышел': 'se pasó el plazo de reclamación',
  'старт {day}': 'empieza el {day}',
  'считаем…': 'calculando…',
  'твой выбор влияет на результат': 'tu elección influye en el resultado',
  'твой голос': 'tu voto',
  'твоя работа': 'tu obra',
  'твоё время по этой ссылке': 'tu tiempo con este enlace',
  'твоё место': 'tu puesto',
  'ты': 'tú',
  'ты уже воспользовался': 'ya la has usado',
  'уже заполнено': 'ya está relleno',
  'файл не открывается — посмотреть по ссылке': 'el archivo no se abre: míralo por el enlace',
  'хеш не опубликован': 'el hash no está publicado',
  'цепочка пройдена': 'cadena completada',
  'шаги проходятся по порядку — награда за каждый начисляется сразу': 'los pasos van en orden y la recompensa de cada uno se otorga al instante',
  'шанс одного билета — {chance}%': 'probabilidad de un boleto: {chance}%',
  'этот код уже был': 'este código ya se usó',
  '— не заполнено': '— sin rellenar',
  'билет|билета|билетов': 'boleto|boletos',
  'участник|участника|участников': 'participante|participantes',
  'акция|акции|акций': 'campaña|campañas',
  'бренд|бренда|брендов': 'marca|marcas',
  'страна|страны|стран': 'país|países',
  'Новичок': 'Novato',
  'Собиратель': 'Coleccionista',
  'Охотник': 'Cazador',
  'Коллекционер': 'Curador',
  'Легенда': 'Leyenda',
  'Центр': 'Centro',
  'Около центра': 'Cerca del centro',
  'Середина': 'Medio',
  'У края': 'Junto al borde',
  'Край · джекпот': 'Borde · bote',
  'начисляются за каждый принятый код — баланс всегда равен сумме истории': 'se otorgan por cada código aceptado: el saldo siempre es la suma del historial',
  'США': 'Estados Unidos',
  'Великобритания': 'Reino Unido',
  'Германия': 'Alemania',
  'Франция': 'Francia',
  'Испания': 'España',
  'Япония': 'Japón',
  'Южная Корея': 'Corea del Sur',
  'Сингапур': 'Singapur',
  'ОАЭ': 'Emiratos Árabes Unidos',
  'Саудовская Аравия': 'Arabia Saudí',
  'Катар': 'Catar',
  'Египет': 'Egipto',
  'Область': 'Región',
  'не указана': 'sin indicar',
  'Область обновлена': 'Región actualizada',
  'Акции, объявленные по штатам и областям, покажутся те, что идут у тебя. Остальные идут по всей стране.': 'De las campañas anunciadas por estados o regiones verás las que se celebran donde estás. El resto son de ámbito nacional.',
  'Это твой бренд?': '¿Es tuya esta marca?',

  // Шаги «как участвовать» — свои у каждой механики, плюс блок честности у
  // механик без случайности и знакомство после первого входа. Общий текст
  // про код врал половине акций: у оффера, анкеты и приза на упаковке
  // кода нет.
  'Открой акцию': 'Abre la campaña',
  'Покупка и код не нужны.': 'No hace falta comprar ni código.',
  'Нажми «Получить»': 'Pulsa «Obtener»',
  'Предложение откроется сразу, по ссылке.': 'La oferta se abre al momento, por enlace.',
  'Пользуйся': 'Disfrútalo',
  'Всего предложений: {n}, по одному на человека.': 'Hay {n} ofertas en total, una por persona.',
  'Количество не ограничено.': 'Sin límite de cantidad.',
  'Купи товар': 'Compra el producto',
  'Приз напечатан {place} — производителем, до покупки.': 'El premio viene impreso {place}, por el fabricante, antes de la compra.',
  'Посмотри, что выпало': 'Mira qué te ha salido',
  'Платформа исход не выбирает: он уже на упаковке.': 'La plataforma no elige el resultado: ya está en el envase.',
  'Приди с упаковкой': 'Ven con el envase',
  'В точку из списка ниже. Сколько призов выдано — видно там же.': 'A un punto de la lista de abajo. Allí mismo se ve cuántos premios se han entregado.',
  'Посмотри, что просят': 'Mira qué te piden',
  'Список полей и твои значения — до передачи.': 'La lista de campos y tus propios valores, antes de entregarlos.',
  'Реши сам': 'Decide tú',
  'Откажешься — ничего не произойдёт. Награда даётся один раз.': 'Si dices no, no pasa nada. La recompensa se da una sola vez.',
  'Открой квиз': 'Abre el test',
  'Ответь на вопросы': 'Responde las preguntas',
  'Правильные ответы открываются сразу.': 'Las respuestas correctas se muestran al momento.',
  'Получи баллы': 'Recibe los puntos',
  'Начисляются за попытку, одну на человека.': 'Se otorgan por un intento, uno por persona.',
  'Отмечайся каждый день': 'Marca cada día',
  'Пропуск обнуляет серию.': 'Un día perdido reinicia la racha.',
  'Получи приз': 'Recibe el premio',
  'Когда серия дойдёт до конца.': 'Cuando completes la racha.',
  'Возьми свою ссылку': 'Coge tu enlace',
  'Она привязана к тебе и видна в акции.': 'Es tuyo y se ve en la campaña.',
  'Позови друга': 'Invita a un amigo',
  'Баллы начислятся, когда он поучаствует.': 'Los puntos llegan cuando participe.',
  'И ты, и друг — суммы объявлены заранее.': 'Tú y tu amigo: las cantidades se anuncian de antemano.',
  'Открой цепочку': 'Abre la cadena',
  'Шаги видны целиком до начала.': 'Todos los pasos se ven antes de empezar.',
  'Проходи шаги по порядку': 'Haz los pasos en orden',
  'Пропустить нельзя, награда за шаг — сразу.': 'No se puede saltar ninguno; cada paso paga al momento.',
  'Забери приз': 'Recoge el premio',
  'За всю цепочку, после последнего шага.': 'Por la cadena completa, tras el último paso.',
  'Внеси вклад': 'Aporta lo tuyo',
  'Нужно {n} участий — счётчик общий и открытый.': 'Hacen falta {n} participaciones; el contador es común y abierto.',
  'Счётчик общий и открытый.': 'El contador es común y abierto.',
  'Получите все': 'Lo recibís todos',
  'Приз достаётся каждому участнику, включая первых.': 'El premio es para cada participante, incluidos los primeros.',
  'Участвуй раньше': 'Participa pronto',
  'Ступень скидки зависит от номера участия.': 'El tramo de descuento depende de tu número de participación.',
  'Получи цену': 'Consigue el precio',
  'Когда ступени кончатся, код не потратится.': 'Cuando se agoten los tramos, tu código no se gasta.',
  'без случайности': 'sin azar',
  'Платформа ничего не разыгрывает: результат определён условиями, объявленными до старта. Сида в этой механике нет, и его хеш ничего бы не доказывал.': 'La plataforma no sortea nada: el resultado lo fijan las condiciones anunciadas antes del inicio. Esta mecánica no tiene semilla, y su hash no demostraría nada.',
  'Проверяется другое: счётчик считается из тех же строк, по которым начисляется награда, а хеш правил не даёт переписать условия задним числом.': 'Aquí se comprueba otra cosa: el contador se calcula con las mismas filas con las que se pagan las recompensas, y el hash de las reglas impide reescribir las condiciones a posteriori.',
  'Женский': 'Femenino',
  'Мужской': 'Masculino',
  'Не указываю': 'Prefiero no decirlo',
  'Выбери': 'Elige',
  'Это нужно при выдаче приза: как к тебе обратиться и как записано в документе. Остальное бренд спросит сам, если его акции это нужно, — и ты увидишь, что именно передаёшь.': 'Esto hace falta al entregar un premio: cómo dirigirnos a ti y cómo figura tu nombre en el documento. Lo demás lo pedirá la marca por su cuenta, si su campaña lo necesita, y verás exactamente qué entregas.',
};

const DICTS: Record<Lang, Dict> = { ru: {}, en, es };

/**
 * Перевод строки.
 *
 * Испанский откатывается на английский, а не на русский: человек, выбравший
 * español, скорее прочтёт английскую строку, чем кириллицу. Неизвестная
 * строка возвращается как есть — показать ключ или пустоту значит сломать
 * экран ради принципа.
 */
export function translate(lang: Lang, text: string): string {
  if (lang === 'ru') return text;
  const hit = DICTS[lang][text];
  if (hit !== undefined) return hit;
  if (lang !== 'en') return en[text] ?? text;
  return text;
}

/** Сколько строк переведено — по этому числу видно, что словарь отстаёт. */
export function dictSize(lang: Lang): number {
  return Object.keys(DICTS[lang]).length;
}
